import { Resource, Booking, Project, AppSettings, Client, CostCentre, ProjectDetails, Absence, BankHoliday, BookingAuditEntry } from '@/types';
import { type PlanPreviewScenario } from '@/types/resourceRequest';
import { getProjectColor } from './utils/calendar';
import { generateDefaultFieldConfigurations, generateDefaultFieldOrder, generateDefaultTabConfigurations } from '@/utils/field-configurations';
import { generateLegacyFieldConfigurations } from '@/utils/field-configurations-legacy';
import { UNALLOCATED_RESOURCE } from '@/utils/unallocated-booking';

// Mock data for Resource Scheduler

/** 'yyyy-MM-dd' a number of days from today, so demo contract windows stay in view. */
const daysFromToday = (offset: number): string => {
  const date = new Date();
  date.setDate(date.getDate() + offset);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

export const mockResources: Resource[] = [
  UNALLOCATED_RESOURCE,
  {
    id: '1',
    name: 'Sarah Johnson',
    role: 'Senior Developer',
    status: 'available',
    type: 'permanent',
    photoUrl: 'https://i.pravatar.cc/150?img=1',
    email: 'sarah.johnson@company.com',
    department: 'Engineering',
    schedulerGroup: 'Team Alpha',
    location: 'New York',
    skills: [{ name: 'React', rating: 5 }, { name: 'TypeScript', rating: 4 }, { name: 'Node.js', rating: 4 }],
    level: 'Senior',
  },
  {
    id: '2',
    name: 'Michael Chen',
    role: 'UI/UX Designer',
    status: 'busy',
    type: 'permanent',
    photoUrl: 'https://i.pravatar.cc/150?img=12',
    email: 'michael.chen@company.com',
    department: 'Design',
    schedulerGroup: 'Team Beta',
    location: 'San Francisco',
    skills: [{ name: 'Figma', rating: 5 }, { name: 'Adobe XD', rating: 4 }, { name: 'User Research', rating: 4 }],
    level: 'Mid-Level',

    contractStartDate: daysFromToday(-20),
    contractEndDate: daysFromToday(-3),
  },
  {
    id: '3',
    name: 'Emily Rodriguez',
    role: 'Project Manager',
    status: 'available',
    type: 'permanent',
    photoUrl: 'https://i.pravatar.cc/150?img=5',
    email: 'emily.rodriguez@company.com',
    department: 'Operations',
    schedulerGroup: 'Team Alpha',
    location: 'Austin',
    skills: [{ name: 'Agile', rating: 5 }, { name: 'Scrum', rating: 4 }, { name: 'Stakeholder Management', rating: 4 }],
    level: 'Mid-Level',

    contractEndDate: daysFromToday(-8),
  },
  {
    id: '4',
    name: 'David Kim',
    role: 'Backend Developer',
    status: 'active',
    type: 'contractor',
    photoUrl: 'https://i.pravatar.cc/150?img=13',
    email: 'david.kim@company.com',
    department: 'Engineering',
    schedulerGroup: 'Team Alpha',
    location: 'Seattle',
    skills: [{ name: 'Python', rating: 5 }, { name: 'Django', rating: 4 }, { name: 'PostgreSQL', rating: 4 }],
    level: 'Mid-Level',
    contractStartDate: daysFromToday(-12),
    contractEndDate: daysFromToday(10),
    useCustomWorkingProfile: true,
    customWorkingProfile: {
      monday: 7.5,
      tuesday: 7.5,
      wednesday: 7.5,
      thursday: 7.5,
      friday: 0,
      saturday: 0,
      sunday: 0,
    },
  },
  {
    id: '17',
    name: 'Alex Turner',
    role: 'Frontend Developer',
    status: 'available',
    type: 'permanent',
    photoUrl: 'https://i.pravatar.cc/150?img=68',
    email: 'alex.turner@company.com',
    department: 'Engineering',
    schedulerGroup: 'Team Alpha',
    location: 'New York Office',
    skills: [{ name: 'React', rating: 5 }, { name: 'TypeScript', rating: 5 }, { name: 'CSS', rating: 4 }, { name: 'Next.js', rating: 3 }],
    level: 'Mid-Level',
  },
  {
    id: '5',
    name: 'Jessica Williams',
    role: 'QA Engineer',
    status: 'available',
    type: 'contractor',
    photoUrl: 'https://i.pravatar.cc/150?img=9',
    email: 'jessica.williams@company.com',
    department: 'Quality Assurance',
    schedulerGroup: 'Team Gamma',
    location: 'Boston',
    skills: [{ name: 'Test Automation', rating: 5 }, { name: 'Selenium', rating: 4 }, { name: 'Jest', rating: 4 }],
    level: 'Mid-Level',
  },
  {
    id: '6',
    name: 'Robert Taylor',
    role: 'DevOps Engineer',
    status: 'busy',
    type: 'agency',
    photoUrl: 'https://i.pravatar.cc/150?img=14',
    email: 'robert.taylor@company.com',
    department: 'Infrastructure',
    schedulerGroup: 'Team Beta',
    location: 'Chicago',
    skills: [{ name: 'Docker', rating: 5 }, { name: 'Kubernetes', rating: 4 }, { name: 'AWS', rating: 5 }],
    level: 'Senior',
  },
  {
    id: '7',
    name: 'Amanda Martinez',
    role: 'Frontend Developer',
    status: 'available',
    type: 'permanent',
    photoUrl: 'https://i.pravatar.cc/150?img=20',
    email: 'amanda.martinez@company.com',
    department: 'Engineering',
    schedulerGroup: 'Team Beta',
    location: 'Miami',
    skills: [{ name: 'Vue.js', rating: 4 }, { name: 'CSS', rating: 5 }, { name: 'JavaScript', rating: 5 }],
    level: 'Mid-Level',
  },
  {
    id: '8',
    name: 'James Anderson',
    role: 'Data Analyst',
    status: 'available',
    type: 'agency',
    photoUrl: 'https://i.pravatar.cc/150?img=33',
    email: 'james.anderson@company.com',
    department: 'Operations',
    schedulerGroup: 'Team Gamma',
    location: 'Denver',
    skills: [{ name: 'SQL', rating: 5 }, { name: 'Python', rating: 4 }, { name: 'Tableau', rating: 4 }],
    level: 'Senior',
  },
  // Additional resources
  {
    id: '9',
    name: 'Lisa Thompson',
    role: 'Senior Developer',
    status: 'available',
    type: 'permanent',
    photoUrl: 'https://i.pravatar.cc/150?img=25',
    email: 'lisa.thompson@company.com',
    department: 'Engineering',
    schedulerGroup: 'Team Alpha',
    location: 'Portland',
    skills: [{ name: 'Java', rating: 5 }, { name: 'Spring Boot', rating: 4 }, { name: 'Microservices', rating: 4 }],
    level: 'Senior',
  },
  {
    id: '10',
    name: 'Daniel Brown',
    role: 'UI/UX Designer',
    status: 'available',
    type: 'permanent',
    photoUrl: 'https://i.pravatar.cc/150?img=52',
    email: 'daniel.brown@company.com',
    department: 'Design',
    schedulerGroup: 'Team Beta',
    location: 'Los Angeles',
    skills: [{ name: 'Sketch', rating: 4 }, { name: 'InVision', rating: 4 }, { name: 'Prototyping', rating: 5 }],
    level: 'Mid-Level',
  },
  {
    id: '11',
    name: 'Rachel Green',
    role: 'Business Analyst',
    status: 'available',
    type: 'permanent',
    photoUrl: 'https://i.pravatar.cc/150?img=32',
    email: 'rachel.green@company.com',
    department: 'Operations',
    schedulerGroup: 'Team Gamma',
    location: 'Atlanta',
    skills: [{ name: 'Requirements Analysis', rating: 5 }, { name: 'JIRA', rating: 4 }, { name: 'Process Mapping', rating: 4 }],
    level: 'Mid-Level',
  },
  {
    id: '12',
    name: 'Chris Wilson',
    role: 'Backend Developer',
    status: 'busy',
    type: 'contractor',
    photoUrl: 'https://i.pravatar.cc/150?img=57',
    email: 'chris.wilson@company.com',
    department: 'Engineering',
    schedulerGroup: 'Team Alpha',
    location: 'Remote',
    skills: [{ name: 'C#', rating: 5 }, { name: '.NET Core', rating: 4 }, { name: 'Azure', rating: 4 }],
    level: 'Mid-Level',
  },
  {
    id: '13',
    name: 'Sophie Turner',
    role: 'QA Engineer',
    status: 'available',
    type: 'permanent',
    photoUrl: 'https://i.pravatar.cc/150?img=44',
    email: 'sophie.turner@company.com',
    department: 'Quality Assurance',
    schedulerGroup: 'Team Gamma',
    location: 'Philadelphia',
    skills: [{ name: 'Manual Testing', rating: 4 }, { name: 'API Testing', rating: 5 }, { name: 'Postman', rating: 4 }],
    level: 'Mid-Level',
  },
  {
    id: '14',
    name: 'Mark Davis',
    role: 'DevOps Engineer',
    status: 'available',
    type: 'permanent',
    photoUrl: 'https://i.pravatar.cc/150?img=60',
    email: 'mark.davis@company.com',
    department: 'Infrastructure',
    schedulerGroup: 'Team Beta',
    location: 'Dallas',
    skills: [{ name: 'Jenkins', rating: 5 }, { name: 'Terraform', rating: 4 }, { name: 'CI/CD', rating: 5 }],
    level: 'Senior',
  },
  {
    id: '15',
    name: 'Emma White',
    role: 'Frontend Developer',
    status: 'available',
    type: 'agency',
    photoUrl: 'https://i.pravatar.cc/150?img=47',
    email: 'emma.white@company.com',
    department: 'Engineering',
    schedulerGroup: 'Team Beta',
    location: 'Phoenix',
    skills: [{ name: 'Angular', rating: 4 }, { name: 'RxJS', rating: 4 }, { name: 'SCSS', rating: 5 }],
    level: 'Mid-Level',
  },
  {
    id: '16',
    name: 'Alex Morgan',
    role: 'Senior Developer',
    status: 'available',
    type: 'permanent',
    photoUrl: 'https://i.pravatar.cc/150?img=68',
    email: 'alex.morgan@company.com',
    department: 'Engineering',
    schedulerGroup: 'Team Alpha',
    location: 'London',
    skills: [{ name: 'React', rating: 5 }, { name: 'TypeScript', rating: 5 }, { name: 'Node.js', rating: 4 }],
    level: 'Senior',
  },
  {
    id: '18',
    name: 'John Doe',
    role: 'Team Member',
    status: 'available',
    type: 'permanent',
    photoUrl: 'https://i.pravatar.cc/150?img=70',
    email: 'john.doe@company.com',
    department: 'Engineering',
    schedulerGroup: 'Team Alpha',
    location: 'Remote',
    skills: [{ name: 'JavaScript', rating: 3 }, { name: 'React', rating: 3 }, { name: 'CSS', rating: 3 }],
    level: 'Junior',
  },
  {
    id: '19',
    name: 'Priya Natarajan',
    role: 'Data Engineer',
    status: 'available',
    type: 'contractor',
    photoUrl: 'https://i.pravatar.cc/150?img=45',
    email: 'priya.natarajan@contractors.com',
    department: 'Engineering',
    schedulerGroup: 'Team Beta',
    location: 'Remote',
    skills: [{ name: 'Python', rating: 5 }, { name: 'Azure Data Factory', rating: 4 }, { name: 'SQL', rating: 5 }],
    level: 'Senior',
  },
  // Assets carry no email, skills or level — they are equipment, booked like people.
  {
    id: '20',
    name: 'Mobile Survey Rig',
    role: 'Survey Equipment',
    status: 'available',
    type: 'asset',
    department: 'Operations',
    schedulerGroup: 'Assets',
    location: 'Austin',
  },
  {
    id: '21',
    name: 'Crane CR-118',
    role: 'Heavy Plant',
    status: 'available',
    type: 'asset',
    department: 'Operations',
    schedulerGroup: 'Assets',
    location: 'London',
  },
  {
    id: '22',
    name: 'Test Lab Bay 2',
    role: 'Facility',
    status: 'available',
    type: 'asset',
    department: 'Engineering',
    schedulerGroup: 'Assets',
    location: 'New York',
  },
];

// Generate some sample bookings across multiple weeks
const today = new Date();
today.setHours(0, 0, 0, 0);

// Returns the number of days from today to Monday of the current week (negative = past).
// Monday = 1, so: Sun=0→-6, Mon=1→0, Tue=2→-1, Wed=3→-2, Thu=4→-3, Fri=5→-4, Sat=6→-5
const getMondayOffset = (): number => {
  const dow = today.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
  return dow === 0 ? -6 : 1 - dow;
};

// Helper function to get a date with time offset from today
// This allows bookings to span multiple weeks so they're visible regardless of which week is viewed
// Returns ISO-like string WITHOUT "Z" suffix to prevent UTC interpretation and timezone offset
const getDateTimeOffset = (days: number, hours: number, minutes: number = 0): string => {
  const date = new Date(today);
  date.setDate(date.getDate() + days);
  date.setHours(hours, minutes, 0, 0);
  // Format as local ISO string without Z suffix to prevent timezone conversion
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${h}:${m}:00.000`;
};

// Helper function for full-day bookings (12:00 AM to 12:00 AM next day)
const getFullDayStart = (days: number): string => {
  const date = new Date(today);
  date.setDate(date.getDate() + days);
  date.setHours(0, 0, 0, 0);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}T00:00:00.000`;
};

const getFullDayEnd = (days: number): string => {
  const date = new Date(today);
  date.setDate(date.getDate() + days + 1); // Next day at midnight
  date.setHours(0, 0, 0, 0);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}T00:00:00.000`;
};

// Helper function for multi-day full-day bookings
const getMultiDayEnd = (startDays: number, durationDays: number): string => {
  const date = new Date(today);
  date.setDate(date.getDate() + startDays + durationDays);
  date.setHours(0, 0, 0, 0);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}T00:00:00.000`;
};

// Helper to produce an ISO timestamp offset from today (for audit history)
const getAuditDate = (daysAgo: number, hours = 9, minutes = 0): string => {
  const date = new Date(today);
  date.setDate(date.getDate() - daysAgo);
  date.setHours(hours, minutes, 0, 0);
  return date.toISOString();
};

// ─── Shared audit personas ────────────────────────────────────────────────────
const AUDIT_USERS = {
  sarah:   { performedBy: 'Sarah Johnson',   performedByInitials: 'SJ', performedByAvatar: 'https://i.pravatar.cc/150?img=1'  },
  michael: { performedBy: 'Michael Chen',    performedByInitials: 'MC', performedByAvatar: 'https://i.pravatar.cc/150?img=12' },
  emily:   { performedBy: 'Emily Rodriguez', performedByInitials: 'ER', performedByAvatar: 'https://i.pravatar.cc/150?img=5'  },
  david:   { performedBy: 'David Kim',       performedByInitials: 'DK', performedByAvatar: 'https://i.pravatar.cc/150?img=13' },
  admin:   { performedBy: 'James Fletcher',  performedByInitials: 'JF', performedByAvatar: 'https://i.pravatar.cc/150?img=53' },
};

// ─── Audit history blocks ─────────────────────────────────────────────────────

const auditHistory_b1: BookingAuditEntry[] = [
  {
    id: 'ah-b1-1',
    action: 'created',
    ...AUDIT_USERS.sarah,
    timestamp: getAuditDate(14, 9, 12),
    changes: [],
    note: 'Booking raised for Sprint 5 kick-off.',
  },
  {
    id: 'ah-b1-2',
    action: 'updated',
    ...AUDIT_USERS.sarah,
    timestamp: getAuditDate(12, 11, 45),
    changes: [
      { field: 'Location', oldValue: 'Remote', newValue: 'New York Office' },
      { field: 'Work Type', oldValue: 'Remote', newValue: 'On-site' },
    ],
  },
  {
    id: 'ah-b1-3',
    action: 'approved',
    ...AUDIT_USERS.admin,
    timestamp: getAuditDate(11, 14, 30),
    changes: [
      { field: 'Status', oldValue: 'Planned', newValue: 'Confirmed' },
    ],
    note: 'Approved following client sign-off on scope.',
  },
  {
    id: 'ah-b1-4',
    action: 'updated',
    ...AUDIT_USERS.emily,
    timestamp: getAuditDate(5, 10, 0),
    changes: [
      { field: 'Priority', oldValue: 'Medium', newValue: 'High' },
      { field: 'Details', oldValue: 'Sprint 5 - Focus on responsive design', newValue: 'Sprint 5 - Responsive design + accessibility pass' },
    ],
    note: 'Priority raised after client escalation.',
  },
];

const auditHistory_b2: BookingAuditEntry[] = [
  {
    id: 'ah-b2-1',
    action: 'created',
    ...AUDIT_USERS.michael,
    timestamp: getAuditDate(20, 10, 0),
    changes: [],
  },
  {
    id: 'ah-b2-2',
    action: 'rescheduled',
    ...AUDIT_USERS.michael,
    timestamp: getAuditDate(10, 9, 15),
    changes: [
      { field: 'Start Date', oldValue: getAuditDate(5, 9).slice(0, 10), newValue: getAuditDate(0, 9).slice(0, 10) },
      { field: 'End Date',   oldValue: getAuditDate(4, 17).slice(0, 10), newValue: getAuditDate(-1, 17).slice(0, 10) },
    ],
    note: 'Client requested earlier delivery of initial mockups.',
  },
  {
    id: 'ah-b2-3',
    action: 'resource_changed',
    ...AUDIT_USERS.admin,
    timestamp: getAuditDate(7, 13, 0),
    changes: [
      { field: 'Resource', oldValue: 'Jessica Williams', newValue: 'Michael Chen' },
    ],
    note: 'Reassigned following team restructure.',
  },
  {
    id: 'ah-b2-4',
    action: 'approved',
    ...AUDIT_USERS.admin,
    timestamp: getAuditDate(7, 15, 45),
    changes: [
      { field: 'Status', oldValue: 'Planned', newValue: 'Confirmed' },
    ],
  },
];

const auditHistory_b3: BookingAuditEntry[] = [
  {
    id: 'ah-b3-1',
    action: 'created',
    ...AUDIT_USERS.emily,
    timestamp: getAuditDate(18, 8, 30),
    changes: [],
    note: 'Q2 planning session — 3 day block reserved.',
  },
  {
    id: 'ah-b3-2',
    action: 'updated',
    ...AUDIT_USERS.emily,
    timestamp: getAuditDate(15, 14, 0),
    changes: [
      { field: 'Description', oldValue: 'Q2 roadmap planning', newValue: 'Q2 roadmap & OKR alignment' },
      { field: 'Location', oldValue: 'London Office', newValue: 'Zoom' },
    ],
  },
  {
    id: 'ah-b3-3',
    action: 'status_changed',
    ...AUDIT_USERS.admin,
    timestamp: getAuditDate(3, 9, 0),
    changes: [
      { field: 'Status', oldValue: 'Planned', newValue: 'Confirmed' },
    ],
  },
];

const auditHistory_b4: BookingAuditEntry[] = [
  {
    id: 'ah-b4-1',
    action: 'created',
    ...AUDIT_USERS.david,
    timestamp: getAuditDate(30, 11, 0),
    changes: [],
    note: 'Initial booking raised for API Integration phase 2.',
  },
  {
    id: 'ah-b4-2',
    action: 'updated',
    ...AUDIT_USERS.david,
    timestamp: getAuditDate(22, 10, 30),
    changes: [
      { field: 'Billing Classification', oldValue: 'Internal', newValue: 'Billable' },
      { field: 'Cost Centre', oldValue: '(none)', newValue: 'CC-001 Engineering' },
      { field: 'Customer', oldValue: '(none)', newValue: 'Global Finance Ltd' },
    ],
  },
  {
    id: 'ah-b4-3',
    action: 'resource_changed',
    ...AUDIT_USERS.admin,
    timestamp: getAuditDate(20, 14, 0),
    changes: [
      { field: 'Resource', oldValue: 'Sarah Johnson', newValue: 'David Kim' },
    ],
    note: 'Reassigned — Sarah has a scheduling conflict with the London sprint.',
  },
  {
    id: 'ah-b4-4',
    action: 'rescheduled',
    ...AUDIT_USERS.admin,
    timestamp: getAuditDate(16, 15, 20),
    changes: [
      { field: 'Start Date', oldValue: getAuditDate(10, 9).slice(0, 10), newValue: getAuditDate(0, 9).slice(0, 10) },
      { field: 'End Date',   oldValue: getAuditDate(9, 17).slice(0, 10), newValue: getAuditDate(-1, 17).slice(0, 10) },
      { field: 'Duration',   oldValue: 'Full Day', newValue: 'Full Day' },
    ],
    note: 'Moved forward — dependency on auth service resolved early.',
  },
  {
    id: 'ah-b4-5',
    action: 'approved',
    ...AUDIT_USERS.admin,
    timestamp: getAuditDate(15, 9, 0),
    changes: [
      { field: 'Status', oldValue: 'Pending Approval', newValue: 'Confirmed' },
    ],
    note: 'Approved following sign-off from Head of Engineering.',
  },
  {
    id: 'ah-b4-6',
    action: 'updated',
    ...AUDIT_USERS.david,
    timestamp: getAuditDate(8, 11, 15),
    changes: [
      { field: 'Details', oldValue: 'REST API endpoints development', newValue: 'REST API endpoints + OpenAPI spec documentation' },
      { field: 'Priority', oldValue: 'Medium', newValue: 'High' },
      { field: 'Location', oldValue: 'Remote', newValue: 'Manchester Office' },
    ],
    note: 'Priority raised after client escalation call this morning.',
  },
  {
    id: 'ah-b4-7',
    action: 'status_changed',
    ...AUDIT_USERS.admin,
    timestamp: getAuditDate(1, 8, 0),
    changes: [
      { field: 'Status', oldValue: 'Confirmed', newValue: 'In Progress' },
    ],
  },
];

// Helper to get the Monday of the current week as a base offset
// so all relative bookings naturally cluster around "this week" and nearby weeks
const mondayOffset = getMondayOffset();

export const mockBookings: Booking[] = [
  // ===========================================================================
  // GROUPING EXAMPLES — visible in the opening week
  // ===========================================================================
  {
    id: 'b-demo-repeat-parent',
    resourceId: '3',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc36',
    description: 'Weekly planning session',
    startTime: getDateTimeOffset(mondayOffset + 1, 10, 0),
    endTime: getDateTimeOffset(mondayOffset + 1, 11, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'hourly',
    durationHours: 1,
    billingClassification: 'internal',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    bookingTypeId: 'bt-4',
    recurrenceGroupId: 'demo-repeat-group',
    isRecurrenceParent: true,
    recurrence: {
      pattern: 'weekly',
      interval: 1,
      endType: 'after',
      occurrences: 2,
      daysOfWeek: ['tuesday'],
    },
  },
  {
    id: 'b-demo-repeat-child',
    resourceId: '3',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc36',
    description: 'Weekly planning session',
    startTime: getDateTimeOffset(mondayOffset + 3, 10, 0),
    endTime: getDateTimeOffset(mondayOffset + 3, 11, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'hourly',
    durationHours: 1,
    billingClassification: 'internal',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    bookingTypeId: 'bt-4',
    recurrenceGroupId: 'demo-repeat-group',
    isRecurrenceParent: false,
  },
  {
    id: 'b-demo-demand-1',
    resourceId: '17',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Open Demand plan allocation',
    startTime: getDateTimeOffset(mondayOffset + 2, 9, 0),
    endTime: getDateTimeOffset(mondayOffset + 2, 13, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'hourly',
    durationHours: 4,
    durationDays: 0,
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    bookingTypeId: 'bt-1',
    demandPlanGroupId: 'demo-demand-group',
    demandId: 1,
    source: 'Demand',
  },
  {
    id: 'b-demo-demand-2',
    resourceId: '17',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Open Demand plan allocation',
    startTime: getDateTimeOffset(mondayOffset + 3, 9, 0),
    endTime: getDateTimeOffset(mondayOffset + 3, 13, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'hourly',
    durationHours: 4,
    durationDays: 0,
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    bookingTypeId: 'bt-1',
    demandPlanGroupId: 'demo-demand-group',
    demandId: 1,
    source: 'Demand',
  },
  // ===========================================================================
  // WEEK -2 (TWO WEEKS AGO) — past confirmed work, completed status
  // Demonstrates: completed bookings, historical data, past utilisation
  // ===========================================================================

  // Sarah Johnson — completed website work (past week, completed)
  {
    id: 'b-sj-past-1',
    resourceId: '1',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Website Redesign — Phase 1 frontend delivery',
    startTime: getFullDayStart(mondayOffset - 14),
    endTime: getMultiDayEnd(mondayOffset - 14, 5),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'onsite',
    status: 'completed',
    priority: 'high',
    bookingTypeId: 'bt-1',
    clientId: 'c1',
    customer: 'Acme Corporation',
    costCentreTask: 'WR-P1',
    bookingReference: 'ACM-2024-001-P1',
    location: 'London Office',
    details: 'Phase 1 — landing page, navigation, design system tokens',
    taskDescription: 'Delivered all P1 components on schedule. Client signed off.',
    bookingNotes: 'Phase 2 starts next sprint.',
    auditHistory: auditHistory_b4,
  },

  // David Kim — completed mobile app development (past)
  {
    id: 'b-dk-past-1',
    resourceId: '4',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc20',
    description: 'Mobile App Design — backend API development',
    startTime: getFullDayStart(mondayOffset - 14),
    endTime: getMultiDayEnd(mondayOffset - 14, 10),
    color: getProjectColor('Mobile App Design'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'completed',
    priority: 'high',
    bookingTypeId: 'bt-1',
    clientId: 'c1',
    customer: 'Acme Corporation',
    costCentreTask: 'MAD-BE-01',
    bookingReference: 'ACM-2024-002-BE',
    location: 'Remote',
    details: 'REST API development for mobile app — authentication, profile, content endpoints',
    auditHistory: auditHistory_b4,
  },

  // Michael Chen — completed UX design (past week, completed)
  {
    id: 'b-mc-past-1',
    resourceId: '2',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc3',
    description: 'Homepage & navigation — final UX iteration',
    startTime: getFullDayStart(mondayOffset - 10),
    endTime: getMultiDayEnd(mondayOffset - 10, 5),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'completed',
    priority: 'medium',
    bookingTypeId: 'bt-1',
    clientId: 'c1',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Final design iteration — sign-off received from Acme',
    auditHistory: auditHistory_b2,
  },

  // Emily Rodriguez — completed project management (past)
  {
    id: 'b-er-past-1',
    resourceId: '3',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc3',
    description: 'Website Redesign — Q2 retrospective & Q3 sprint planning',
    startTime: getFullDayStart(mondayOffset - 7),
    endTime: getMultiDayEnd(mondayOffset - 7, 3),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'onsite',
    status: 'completed',
    priority: 'medium',
    bookingTypeId: 'bt-4',
    clientId: 'c1',
    customer: 'Acme Corporation',
    location: 'London Office',
    details: 'Q2 retrospective, velocity review, Q3 backlog grooming with Acme team',
    auditHistory: auditHistory_b3,
  },

  // ===========================================================================
  // WEEK -1 (LAST WEEK) — mix of confirmed + cancelled
  // Demonstrates: cancelled bookings, planned vs confirmed contrast
  // ===========================================================================

  // Jessica Williams — QA testing (last week, confirmed)
  {
    id: 'b-jw-1',
    resourceId: '5',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'API Integration — regression testing, payment gateway',
    startTime: getFullDayStart(mondayOffset - 7),
    endTime: getMultiDayEnd(mondayOffset - 7, 5),
    color: getProjectColor('API Integration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    bookingTypeId: 'bt-1',
    clientId: 'c2',
    customer: 'Global Finance Ltd',
    bookingReference: 'GFL-2024-015-QA2',
    location: 'Remote',
    details: 'Full regression suite — 247 test cases across payment flows',
  },

  // Robert Taylor — cancelled maintenance (last week, cancelled — demonstrates cancel state)
  {
    id: 'b-rt-cancelled-1',
    resourceId: '6',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    costCentreId: 'cc6',
    description: 'Scheduled maintenance window — CANCELLED',
    startTime: getDateTimeOffset(mondayOffset - 5, 22, 0),
    endTime: getDateTimeOffset(mondayOffset - 5, 26, 0),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'hourly',
    billingClassification: 'non-billable',
    workType: 'remote',
    status: 'cancelled',
    priority: 'high',
    bookingTypeId: 'bt-5',
    costCentreTask: 'MAINT-001',
    location: 'Remote',
    details: 'Emergency change freeze — maintenance postponed to next cycle',
    bookingNotes: 'Cancelled due to client-imposed change freeze. Rescheduled for Week 3.',
  },

  // Lisa Thompson — training session (last week, confirmed)
  {
    id: 'b-lt-1',
    resourceId: '9',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc7',
    description: 'Agile Scrum Master certification — Day 1-2',
    startTime: getFullDayStart(mondayOffset - 5),
    endTime: getMultiDayEnd(mondayOffset - 5, 2),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'full-day',
    billingClassification: 'non-billable',
    workType: 'onsite',
    status: 'confirmed',
    priority: 'medium',
    bookingTypeId: 'bt-3',
    clientId: 'c3',
    customer: 'HealthTech Solutions',
    location: 'Edinburgh Training Centre',
    details: 'CSM certification — 2-day intensive course',
    bookingNotes: 'Pre-approved by Head of Delivery. Cert funded from L&D budget.',
  },

  // ===========================================================================
  // CURRENT WEEK — rich variety: HIGH utilisation on Mon–Wed, drops Thu–Fri
  // Demonstrates: high utilisation, conflict booking, hourly bookings, planned status
  // ===========================================================================

  // --- Sarah Johnson (id '1') — HIGH utilisation this week -------------------
  {
    id: 'b-sj-1',
    resourceId: '1',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Website Redesign — frontend architecture review',
    startTime: getFullDayStart(mondayOffset + 0),
    endTime: getMultiDayEnd(mondayOffset + 0, 3),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'onsite',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    clientId: 'c1',
    customer: 'Acme Corporation',
    costCentreTask: 'WR-FE-01',
    bookingReference: 'ACM-2024-001-FE',
    location: 'New York Office',
    details: 'Sprint 5 — responsive component build and architecture review',
    taskDescription: 'Complete the accordion navigation component and review PR backlog',
    bookingNotes: 'Client stakeholders attending Wed afternoon demo.',
    auditHistory: auditHistory_b1,
  },
  // Sarah Johnson — also booked on a SECOND project Thursday (creates high utilisation)
  {
    id: 'b-sj-2',
    resourceId: '1',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc20',
    description: 'Mobile App Design — Phase 2 backend kickoff',
    startTime: getFullDayStart(mondayOffset + 3),
    endTime: getMultiDayEnd(mondayOffset + 3, 2),
    color: getProjectColor('Mobile App Design'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    clientId: 'c1',
    customer: 'Acme Corporation',
    costCentreTask: 'MAD-P2-KO',
    bookingReference: 'ACM-2024-002-P2',
    location: 'Remote',
    details: 'Phase 2 scope handover — push notifications, offline sync, API gateway',
    auditHistory: auditHistory_b4,
  },

  // --- Michael Chen (id '2') — CONFLICT: double-booked Wed ----------------
  {
    id: 'b-mc-1',
    resourceId: '2',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc3',
    description: 'Website Redesign — UX component library',
    startTime: getFullDayStart(mondayOffset + 0),
    endTime: getMultiDayEnd(mondayOffset + 0, 3),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    clientId: 'c1',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Design system tokens, component documentation and Figma handoff',
    auditHistory: auditHistory_b2,
  },
  // Michael Chen — CONFLICT booking (also booked Tue–Thu on different project)
  {
    id: 'b-mc-conflict-1',
    resourceId: '2',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc18',
    description: 'Mobile App Design — onboarding flow wireframes',
    startTime: getFullDayStart(mondayOffset + 1),
    endTime: getMultiDayEnd(mondayOffset + 1, 3),
    color: getProjectColor('Mobile App Design'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    clientId: 'c1',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Onboarding flow — 8 screens, 2 variants each. Prototype in Figma.',
    bookingNotes: 'Conflict with Website Redesign Mon–Wed — needs PM resolution.',
  },

  // --- Emily Rodriguez (id '3') — full week project management ----------------
  {
    id: 'b-er-1',
    resourceId: '3',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc36',
    description: 'Sprint Planning — weekly ceremonies & stakeholder sync',
    startTime: getFullDayStart(mondayOffset + 0),
    endTime: getMultiDayEnd(mondayOffset + 0, 5),
    color: getProjectColor('Sprint Planning'),
    durationType: 'full-day',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    clientId: 'c7',
    customer: 'Internal',
    location: 'Leeds HQ',
    details: 'Sprint ceremonies: daily stand-ups, backlog refinement, sprint review',
    auditHistory: auditHistory_b3,
  },

  // --- David Kim (id '4') — high utilisation with hourly meeting ---------------
  {
    id: 'b-dk-1',
    resourceId: '4',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc2',
    description: 'Website Redesign — backend API & CMS integration',
    startTime: getFullDayStart(mondayOffset + 0),
    endTime: getMultiDayEnd(mondayOffset + 0, 3),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    clientId: 'c1',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Headless CMS setup, content API, search indexing and CDN configuration',
  },
  // David Kim — hourly client call (Wednesday afternoon, demonstrating hourly booking)
  {
    id: 'b-dk-hourly-1',
    resourceId: '4',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc2',
    description: 'Website Redesign — technical review call with Acme team',
    startTime: getDateTimeOffset(mondayOffset + 2, 14, 0),
    endTime: getDateTimeOffset(mondayOffset + 2, 16, 30),
    color: getProjectColor('Website Redesign'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    bookingTypeId: 'bt-4',
    clientId: 'c1',
    customer: 'Acme Corporation',
    location: 'Zoom',
    details: 'Technical architecture review with Acme CTO and engineering lead',
    bookingNotes: 'Agenda shared by client 48h in advance.',
  },
  // David Kim — Thu–Fri on mobile app project (overloaded! Mon–Wed full-day + Thu–Fri)
  {
    id: 'b-dk-2',
    resourceId: '4',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc20',
    description: 'Mobile App Design — backend integration sprint',
    startTime: getFullDayStart(mondayOffset + 3),
    endTime: getMultiDayEnd(mondayOffset + 3, 2),
    color: getProjectColor('Mobile App Design'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'onsite',
    status: 'confirmed',
    priority: 'high',
    bookingTypeId: 'bt-1',
    clientId: 'c1',
    customer: 'Acme Corporation',
    location: 'London Office',
    details: 'Mobile backend integration — push notifications, auth flows, data sync',
  },

  // --- Alex Turner (id '17') — mid-week start, planned status -----------------
  {
    id: 'b-at-1',
    resourceId: '17',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Website Redesign — responsive layout implementation',
    startTime: getFullDayStart(mondayOffset + 1),
    endTime: getMultiDayEnd(mondayOffset + 1, 4),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    bookingTypeId: 'bt-1',
    clientId: 'c1',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Mobile-first responsive layout — header, nav, hero, footer',
    auditHistory: auditHistory_b1,
  },

  // --- Jessica Williams (id '5') — QA regression testing this week ---------------
  {
    id: 'b-jw-2',
    resourceId: '5',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'API Integration — integration test suite execution',
    startTime: getFullDayStart(mondayOffset + 0),
    endTime: getMultiDayEnd(mondayOffset + 0, 5),
    color: getProjectColor('API Integration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    clientId: 'c2',
    customer: 'Global Finance Ltd',
    bookingReference: 'GFL-2024-015-IT',
    location: 'Remote',
    details: 'Full integration test execution across 3 environments — dev, staging, UAT',
  },

  // --- Robert Taylor (id '6') — LOW utilisation: only Thursday ----------------
  // (rest of week is FREE — demonstrates low utilisation contrast)
  {
    id: 'b-rt-1',
    resourceId: '6',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    costCentreId: 'cc6',
    description: 'Infrastructure Setup — CI/CD pipeline review',
    startTime: getDateTimeOffset(mondayOffset + 3, 10, 0),
    endTime: getDateTimeOffset(mondayOffset + 3, 16, 0),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    bookingTypeId: 'bt-1',
    clientId: 'c2',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Review existing Jenkins pipeline, propose GitHub Actions migration plan',
  },

  // --- Lisa Thompson (id '9') — near capacity: 4 days -------------------------
  {
    id: 'b-lt-2',
    resourceId: '9',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc7',
    description: 'Testing Phase 2 — end-to-end test automation',
    startTime: getFullDayStart(mondayOffset + 0),
    endTime: getMultiDayEnd(mondayOffset + 0, 4),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    bookingTypeId: 'bt-1',
    clientId: 'c3',
    customer: 'HealthTech Solutions',
    location: 'Remote',
    details: 'Selenium/Cypress e2e test automation — patient portal and booking flows',
  },

  // --- Amanda Martinez (id '7') — PLANNED status (awaiting confirmation) ------
  {
    id: 'b-am-1',
    resourceId: '7',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    costCentreId: 'cc4',
    description: 'Infrastructure Setup — cloud architecture planning',
    startTime: getFullDayStart(mondayOffset + 0),
    endTime: getMultiDayEnd(mondayOffset + 0, 3),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    bookingTypeId: 'bt-1',
    clientId: 'c2',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Cloud architecture planning — VPC design, IAM, security group policies',
    bookingNotes: 'Awaiting final scope sign-off from product owner.',
  },

  // --- Daniel Brown (id '10') — multi-resource booking with James Anderson ----
  {
    id: 'b-db-1',
    resourceId: '10',
    resourceIds: ['10', '8'],
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc8',
    description: 'Testing Phase 2 — UX review workshop (multi-resource)',
    startTime: getDateTimeOffset(mondayOffset + 2, 9, 0),
    endTime: getDateTimeOffset(mondayOffset + 2, 17, 0),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'onsite',
    status: 'confirmed',
    priority: 'high',
    bookingTypeId: 'bt-4',
    clientId: 'c3',
    customer: 'HealthTech Solutions',
    location: 'Edinburgh Office',
    details: 'Full-day UX review workshop — two specialists collaborating on patient portal flow',
    bookingNotes: 'Both Daniel Brown and James Anderson attending. Room booked for 10.',
    requiredSkills: ['UX Research', 'Prototyping', 'Accessibility'],
  },

  // --- John Doe (id '18') — LOW utilisation, only one half-day meeting --------
  // (demonstrates a resource with very little allocated work)
  {
    id: 'b-jd-1',
    resourceId: '18',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Website Redesign — team stand-up and backlog review',
    startTime: getDateTimeOffset(mondayOffset + 0, 9, 30),
    endTime: getDateTimeOffset(mondayOffset + 0, 11, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'low',
    bookingTypeId: 'bt-4',
    clientId: 'c1',
    customer: 'Acme Corporation',
    location: 'Zoom',
    details: 'Weekly sprint ceremony — estimation and priority review',
  },

  // ===========================================================================
  // WEEK +1 (NEXT WEEK) — forward planning: planned + confirmed mix
  // Demonstrates: upcoming planned work, near-capacity scenarios
  // ===========================================================================

  // Sarah Johnson — next week (planned, full week — near capacity)
  {
    id: 'b-sj-3',
    resourceId: '1',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Website Redesign — Sprint 6 development',
    startTime: getFullDayStart(mondayOffset + 7),
    endTime: getMultiDayEnd(mondayOffset + 7, 5),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    bookingTypeId: 'bt-1',
    clientId: 'c1',
    customer: 'Acme Corporation',
    costCentreTask: 'WR-SP6',
    location: 'Remote',
    details: 'Sprint 6 — checkout flow, payment integration, order confirmation',
  },

  // Michael Chen — next week design work (confirmed, Mon–Wed)
  {
    id: 'b-mc-2',
    resourceId: '2',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc18',
    description: 'Mobile App Design — high-fidelity mockups delivery',
    startTime: getFullDayStart(mondayOffset + 7),
    endTime: getMultiDayEnd(mondayOffset + 7, 3),
    color: getProjectColor('Mobile App Design'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    bookingTypeId: 'bt-1',
    clientId: 'c1',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Hi-fi mockups for all 14 screens — ready for developer handoff',
  },
  // Michael Chen — stakeholder presentation next Friday (hourly)
  {
    id: 'b-mc-3',
    resourceId: '2',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc18',
    description: 'Mobile App Design — client stakeholder presentation',
    startTime: getDateTimeOffset(mondayOffset + 11, 14, 0),
    endTime: getDateTimeOffset(mondayOffset + 11, 16, 0),
    color: getProjectColor('Mobile App Design'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'onsite',
    status: 'planned',
    priority: 'high',
    bookingTypeId: 'bt-4',
    clientId: 'c1',
    customer: 'Acme Corporation',
    location: 'Acme HQ — Boardroom 2',
    details: 'Design review presentation to Acme product board — 2-hour session',
    bookingNotes: 'Bring printed deck + tablet demos. MD attending.',
    requiredSkills: ['Figma', 'Presentation'],
  },

  // Emily Rodriguez — next week project governance
  {
    id: 'b-er-2',
    resourceId: '3',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc3',
    description: 'Website Redesign — Q3 OKR alignment workshop',
    startTime: getFullDayStart(mondayOffset + 7),
    endTime: getMultiDayEnd(mondayOffset + 7, 2),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'onsite',
    status: 'confirmed',
    priority: 'high',
    bookingTypeId: 'bt-4',
    clientId: 'c1',
    customer: 'Acme Corporation',
    location: 'London Office',
    details: 'OKR alignment workshop — all team leads attending',
  },
  // Emily Rodriguez — next week Thu–Fri also booked (near capacity)
  {
    id: 'b-er-3',
    resourceId: '3',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc18',
    description: 'Mobile App Design — UAT coordination',
    startTime: getFullDayStart(mondayOffset + 10),
    endTime: getMultiDayEnd(mondayOffset + 10, 2),
    color: getProjectColor('Mobile App Design'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    bookingTypeId: 'bt-1',
    clientId: 'c1',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'UAT coordination with Acme test team — defect triage and sign-off',
  },

  // David Kim — next week mobile app backend work
  {
    id: 'b-dk-3',
    resourceId: '4',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc20',
    description: 'Mobile App Design — backend services delivery sprint',
    startTime: getFullDayStart(mondayOffset + 7),
    endTime: getMultiDayEnd(mondayOffset + 7, 5),
    color: getProjectColor('Mobile App Design'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'onsite',
    status: 'confirmed',
    priority: 'high',
    bookingTypeId: 'bt-1',
    clientId: 'c1',
    customer: 'Acme Corporation',
    bookingReference: 'ACM-2024-002-BE2',
    location: 'London Office',
    details: 'Backend services delivery — notifications, profile management, search API',
  },

  // Alex Turner — next week (confirmed full week — at capacity)
  {
    id: 'b-at-2',
    resourceId: '17',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Website Redesign — Sprint 6 frontend build',
    startTime: getFullDayStart(mondayOffset + 7),
    endTime: getMultiDayEnd(mondayOffset + 7, 5),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    bookingTypeId: 'bt-1',
    clientId: 'c1',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Checkout flow, basket, payment integration UI',
  },

  // Robert Taylor — next week: Mon–Fri (high utilisation for contrast with this week)
  {
    id: 'b-rt-2',
    resourceId: '6',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    costCentreId: 'cc6',
    description: 'Infrastructure Setup — production cutover',
    startTime: getFullDayStart(mondayOffset + 7),
    endTime: getMultiDayEnd(mondayOffset + 7, 5),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'onsite',
    status: 'confirmed',
    priority: 'high',
    bookingTypeId: 'bt-1',
    clientId: 'c2',
    customer: 'Global Finance Ltd',
    bookingReference: 'GFL-2024-016-CUT',
    location: 'Manchester Data Centre',
    details: 'Production cutover week — live deployment, smoke tests, hypercare',
  },

  // Jessica Williams — next week (low utilisation — only Wednesday)
  {
    id: 'b-jw-3',
    resourceId: '5',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'API Integration — defect review session',
    startTime: getDateTimeOffset(mondayOffset + 9, 10, 0),
    endTime: getDateTimeOffset(mondayOffset + 9, 14, 0),
    color: getProjectColor('API Integration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    bookingTypeId: 'bt-4',
    clientId: 'c2',
    customer: 'Global Finance Ltd',
    location: 'Zoom',
    details: 'Defect triage: P1 review with dev team, P2 backlog grooming',
  },

  // ===========================================================================
  // WEEK +2 (TWO WEEKS AHEAD) — longer planning horizon
  // Demonstrates: longer-range planning, multi-week projects
  // ===========================================================================

  // Sarah Johnson — week +2, 2-week project (overloaded — spans into week +3)
  {
    id: 'b-sj-4',
    resourceId: '1',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc20',
    description: 'Mobile App Design — Phase 2 full delivery sprint',
    startTime: getFullDayStart(mondayOffset + 14),
    endTime: getMultiDayEnd(mondayOffset + 14, 10),
    color: getProjectColor('Mobile App Design'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    bookingTypeId: 'bt-1',
    clientId: 'c1',
    customer: 'Acme Corporation',
    costCentreTask: 'MAD-P2-DEL',
    bookingReference: 'ACM-2024-002-P2D',
    location: 'Remote',
    details: 'Phase 2 full delivery — offline sync, push notifications, performance tuning',
  },

  // Alex Turner — week +2 (planned, full week)
  {
    id: 'b-at-3',
    resourceId: '17',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Website Redesign — React SPA Sprint 7 development',
    startTime: getFullDayStart(mondayOffset + 14),
    endTime: getMultiDayEnd(mondayOffset + 14, 5),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    bookingTypeId: 'bt-1',
    clientId: 'c1',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'React SPA — user account section, order history, self-service portal',
  },

  // Michael Chen — week +2 (near capacity)
  {
    id: 'b-mc-4',
    resourceId: '2',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc18',
    description: 'Mobile App Design — UX audit and redesign',
    startTime: getFullDayStart(mondayOffset + 14),
    endTime: getMultiDayEnd(mondayOffset + 14, 4),
    color: getProjectColor('Mobile App Design'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    bookingTypeId: 'bt-1',
    clientId: 'c1',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Full UX audit of mobile app — accessibility report + redesign proposals',
  },

  // Emily Rodriguez — week +2, internal training (demonstrates non-billable internal)
  {
    id: 'b-er-4',
    resourceId: '3',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc3',
    description: 'Website Redesign — PMP exam preparation (internal)',
    startTime: getFullDayStart(mondayOffset + 14),
    endTime: getMultiDayEnd(mondayOffset + 14, 3),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'non-billable',
    workType: 'onsite',
    status: 'confirmed',
    priority: 'medium',
    bookingTypeId: 'bt-3',
    clientId: 'c1',
    customer: 'Acme Corporation',
    location: 'London Training Centre',
    details: 'PMP exam prep course — 3-day intensive. Exam booked for following week.',
    bookingNotes: 'L&D funded. Pre-approved by Director of Delivery.',
  },

  // Robert Taylor — week +2 (LOW utilisation — free most of week, just Fri)
  {
    id: 'b-rt-3',
    resourceId: '6',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    costCentreId: 'cc6',
    description: 'Infrastructure Setup — post-cutover review',
    startTime: getDateTimeOffset(mondayOffset + 18, 9, 0),
    endTime: getDateTimeOffset(mondayOffset + 18, 13, 0),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    bookingTypeId: 'bt-4',
    clientId: 'c2',
    customer: 'Global Finance Ltd',
    location: 'Zoom',
    details: 'Post-cutover hypercare review — ticket triage and performance metrics',
  },

  // Lisa Thompson — week +2 full week (at near capacity)
  {
    id: 'b-lt-3',
    resourceId: '9',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc9',
    description: 'Testing Phase 2 — performance testing and load analysis',
    startTime: getFullDayStart(mondayOffset + 14),
    endTime: getMultiDayEnd(mondayOffset + 14, 5),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    bookingTypeId: 'bt-1',
    clientId: 'c3',
    customer: 'HealthTech Solutions',
    location: 'Remote',
    details: 'Performance testing — load simulation, bottleneck analysis, tuning report',
  },

  // ===========================================================================
  // WEEK +3 (THREE WEEKS AHEAD) — OVERLOADED: Sarah has two overlapping projects
  // Demonstrates: resource conflict / double-booking scenario
  // ===========================================================================

  // Sarah Johnson — CONFLICT booking A (full week on Website Redesign)
  {
    id: 'b-sj-conflict-a',
    resourceId: '1',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Website Redesign — Sprint 7 development',
    startTime: getFullDayStart(mondayOffset + 21),
    endTime: getMultiDayEnd(mondayOffset + 21, 5),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    bookingTypeId: 'bt-1',
    clientId: 'c1',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Sprint 7 — CMS integration, blog module, SEO optimisations',
  },
  // Sarah Johnson — CONFLICT booking B (also booked Wed–Fri on Mobile App, same week)
  {
    id: 'b-sj-conflict-b',
    resourceId: '1',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc20',
    description: 'Mobile App Design — Phase 2 client sign-off sessions',
    startTime: getFullDayStart(mondayOffset + 23),
    endTime: getMultiDayEnd(mondayOffset + 23, 3),
    color: getProjectColor('Mobile App Design'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'onsite',
    status: 'planned',
    priority: 'high',
    bookingTypeId: 'bt-1',
    clientId: 'c1',
    customer: 'Acme Corporation',
    location: 'London Office',
    details: 'Client sign-off sessions — needs scheduling conflict resolution with PM',
    bookingNotes: 'CONFLICT: Sarah also booked on Website Redesign this week. PM to resolve.',
  },

  // Daniel Brown — week +3 design work
  {
    id: 'b-db-2',
    resourceId: '10',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc8',
    description: 'Testing Phase 2 — accessibility audit and redesign',
    startTime: getFullDayStart(mondayOffset + 21),
    endTime: getMultiDayEnd(mondayOffset + 21, 4),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    bookingTypeId: 'bt-1',
    clientId: 'c3',
    customer: 'HealthTech Solutions',
    location: 'Remote',
    details: 'WCAG 2.1 AA accessibility audit and redesign — patient portal',
  },

  // ===========================================================================
  // UNALLOCATED BOOKING — demonstrates resource request / demand workflow
  // ===========================================================================
  {
    id: 'b-unallocated-1',
    resourceId: 'unallocated',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc2',
    description: 'Website Redesign — senior backend developer needed',
    startTime: getFullDayStart(mondayOffset + 7),
    endTime: getMultiDayEnd(mondayOffset + 7, 10),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    bookingTypeId: 'bt-1',
    clientId: 'c1',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Unallocated demand — requires senior backend engineer for Node.js/AWS work',
    requiredRole: 'Senior Developer',
    requiredSkills: ['Node.js', 'AWS', 'PostgreSQL'],
  },
  {
    id: 'b-unallocated-2',
    resourceId: 'unallocated',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc18',
    description: 'Mobile App Design — iOS specialist needed',
    startTime: getFullDayStart(mondayOffset + 14),
    endTime: getMultiDayEnd(mondayOffset + 14, 5),
    color: getProjectColor('Mobile App Design'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    bookingTypeId: 'bt-1',
    clientId: 'c1',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Unallocated demand — iOS Swift developer for native app implementation',
    requiredRole: 'Frontend Developer',
    requiredSkills: ['Swift', 'iOS', 'Xcode'],
  },
];


export const mockProjects: Project[] = [
  {
    id: 'pd1',
    name: 'Website Redesign',
    code: 'WR-2024-001',
    budgetDays: 40,
    consumedDays: 28,
    color: getProjectColor('Website Redesign'),
    description: 'Complete website redesign with modern UI/UX',
    status: 'in progress',
    clientId: 'c1',
    costCentreIds: ['cc1', 'cc3'],
    companyId: 'company-1',
    roleBudgetHours: [
      { role: 'Frontend Developer', monthlyHours: { '2026-01': 90, '2026-02': 90, '2026-03': 90, '2026-04': 90, '2026-05': 90, '2026-06': 90, '2026-07': 90, '2026-08': 90, '2026-09': 90, '2026-10': 90, '2026-11': 90, '2026-12': 90 } },
      { role: 'UI/UX Designer',     monthlyHours: { '2026-01': 40, '2026-02': 40, '2026-03': 40, '2026-04': 40, '2026-05': 40, '2026-06': 40, '2026-07': 40, '2026-08': 40, '2026-09': 40, '2026-10': 40, '2026-11': 40, '2026-12': 40 } },
      { role: 'Project Manager',    monthlyHours: { '2026-01': 20, '2026-02': 20, '2026-03': 20, '2026-04': 20, '2026-05': 20, '2026-06': 20, '2026-07': 20, '2026-08': 20, '2026-09': 20, '2026-10': 20, '2026-11': 20, '2026-12': 20 } },
    ],
  },
  {
    id: 'pd2',
    name: 'Mobile App Design',
    code: 'MAD-2024-002',
    budgetDays: 30,
    consumedDays: 15,
    color: getProjectColor('Mobile App Design'),
    description: 'Native mobile application design and development',
    status: 'in progress',
    clientId: 'c3',
    costCentreIds: ['cc2', 'cc3'],
    companyId: 'company-1',
    roleBudgetHours: [
      { role: 'UI/UX Designer',       monthlyHours: { '2026-01': 80, '2026-02': 80, '2026-03': 80, '2026-04': 80, '2026-05': 80, '2026-06': 80, '2026-07': 80, '2026-08': 80, '2026-09': 80, '2026-10': 80, '2026-11': 80, '2026-12': 80 } },
      { role: 'Frontend Developer',   monthlyHours: { '2026-01': 60, '2026-02': 60, '2026-03': 60, '2026-04': 60, '2026-05': 60, '2026-06': 60, '2026-07': 60, '2026-08': 60, '2026-09': 60, '2026-10': 60, '2026-11': 60, '2026-12': 60 } },
    ],
  },
  {
    id: 'pd3',
    name: 'API Integration',
    code: 'GFL-2024-015',
    budgetDays: 25,
    consumedDays: 26,
    color: getProjectColor('API Integration'),
    description: 'Integration with third-party payment and authentication APIs',
    status: 'in progress',
    clientId: 'c2',
    costCentreIds: ['cc2', 'cc5', 'cc7'],
    companyId: 'company-2',
    roleBudgetHours: [
      { role: 'Backend Developer', monthlyHours: { '2026-01': 90, '2026-02': 90, '2026-03': 90, '2026-04': 90, '2026-05': 90, '2026-06': 90, '2026-07': 90, '2026-08': 90, '2026-09': 90, '2026-10': 90, '2026-11': 90, '2026-12': 90 } },
      { role: 'DevOps Engineer',   monthlyHours: { '2026-01': 30, '2026-02': 30, '2026-03': 30, '2026-04': 30, '2026-05': 30, '2026-06': 30, '2026-07': 30, '2026-08': 30, '2026-09': 30, '2026-10': 30, '2026-11': 30, '2026-12': 30 } },
    ],
  },
  {
    id: 'pd4',
    name: 'Database Migration',
    code: 'DM-2024-004',
    budgetDays: 20,
    consumedDays: 8,
    color: getProjectColor('Database Migration'),
    description: 'Migration from legacy database to modern cloud solution',
    status: 'in progress',
    clientId: 'c6',
    costCentreIds: ['cc2', 'cc6'],
    companyId: 'company-2',
    roleBudgetHours: [
      { role: 'Backend Developer', monthlyHours: { '2026-01': 70, '2026-02': 70, '2026-03': 70, '2026-04': 70, '2026-05': 70, '2026-06': 70, '2026-07': 70, '2026-08': 70, '2026-09': 70, '2026-10': 70, '2026-11': 70, '2026-12': 70 } },
      { role: 'Senior Developer',  monthlyHours: { '2026-01': 50, '2026-02': 50, '2026-03': 50, '2026-04': 50, '2026-05': 50, '2026-06': 50, '2026-07': 50, '2026-08': 50, '2026-09': 50, '2026-10': 50, '2026-11': 50, '2026-12': 50 } },
    ],
  },
  {
    id: 'pd5',
    name: 'Testing Phase 2',
    code: 'TP2-2024-005',
    budgetDays: 15,
    consumedDays: 14,
    color: getProjectColor('Testing Phase 2'),
    description: 'Comprehensive testing and quality assurance phase 2',
    status: 'in progress',
    clientId: 'c5',
    costCentreIds: ['cc4', 'cc5'],
    companyId: 'company-3',
    roleBudgetHours: [
      { role: 'QA Engineer', monthlyHours: { '2026-01': 80, '2026-02': 80, '2026-03': 80, '2026-04': 80, '2026-05': 80, '2026-06': 80, '2026-07': 80, '2026-08': 80, '2026-09': 80, '2026-10': 80, '2026-11': 80, '2026-12': 80 } },
    ],
  },
  {
    id: 'pd6',
    name: 'Security Audit',
    code: 'SA-2024-006',
    budgetDays: 50,
    consumedDays: 32,
    color: getProjectColor('Security Audit'),
    description: 'Comprehensive security audit and penetration testing',
    status: 'in progress',
    clientId: 'c4',
    costCentreIds: ['cc1', 'cc12'],
    companyId: 'company-3',
  },
  {
    id: 'pd7',
    name: 'Performance Optimization',
    code: 'PO-2024-007',
    budgetDays: 35,
    consumedDays: 18,
    color: getProjectColor('Performance Optimization'),
    description: 'Application performance optimization and speed improvements',
    status: 'in progress',
    clientId: 'c1',
    costCentreIds: ['cc2', 'cc6', 'cc11'],
    companyId: 'company-4',
  },
  {
    id: 'pd8',
    name: 'Sprint Planning',
    code: 'SP-2024-008',
    budgetDays: 20,
    consumedDays: 12,
    color: getProjectColor('Sprint Planning'),
    description: 'Quarterly sprint planning and roadmap development',
    status: 'in progress',
    clientId: 'c7',
    costCentreIds: ['cc3', 'cc4', 'cc13'],
    companyId: 'company-4',
  },
  {
    id: 'pd9',
    name: 'Customer Portal',
    code: 'HTS-2024-013',
    budgetDays: 45,
    consumedDays: 38,
    color: getProjectColor('Customer Portal'),
    description: 'Self-service customer portal with account management',
    status: 'in progress',
    clientId: 'c3',
    costCentreIds: ['cc2', 'cc6', 'cc10'],
    companyId: 'company-5',
  },
  {
    id: 'pd10',
    name: 'Marketing Website',
    code: 'GFL-2024-020',
    budgetDays: 25,
    consumedDays: 22,
    color: getProjectColor('Marketing Website'),
    description: 'New marketing website with CMS integration',
    status: 'planning',
    clientId: 'c2',
    costCentreIds: ['cc8', 'cc3', 'cc14'],
    companyId: 'company-5',
  },
];

export const defaultSettings: AppSettings = {
  defaultCalendarView: 'month',
  businessHoursStart: 8,
  businessHoursEnd: 18,
  visibleHoursStart: 7,
  visibleHoursEnd: 19,
  timeSlotInterval: 30,
  // Global Working Hours Profile
  globalWorkingProfile: {
    monday: 7.5,
    tuesday: 7.5,
    wednesday: 7.5,
    thursday: 7.5,
    friday: 7.5,
    saturday: 0,
    sunday: 0,
  },
  // Administrator Settings
  showAbsence: true,
  absenceLabelOverride: undefined,
  absenceColor: '#94a3b8',
  showPublicHolidays: true,
  publicHolidayColor: '#fbbf24',
  groupingMethod: 'scheduler-group',
  confirmDragDrop: false, // Default to no confirmation (users can enable if desired)
  defaultToLoggedInResource: false,
  doNotCopyUserFields: false,
  showResourceRates: true,
  copyResourceRates: true,
  defaultTimezone: 'Europe/London', // UK timezone as default
  enableTimezones: false, // Multiple timezones disabled by default
  availableTimezones: [], // Empty when timezones are disabled
  quickAddDefault: 'bt-1', // Default to 'Project' booking type
  descriptionDefault: 'none',
  availableProjectStatuses: ['in progress'],
  availableCostCentreStatuses: ['in progress'],
  statusConfigs: [
    { id: 'planned', text: 'Planned', color: '#9FE1CB', enabled: true },
    { id: 'confirmed', text: 'Confirmed', color: '#CECBF6', enabled: true },
    { id: 'cancelled', text: 'Cancelled', color: '#F5C4B3', enabled: true },
    { id: 'completed', text: 'Completed', color: '#B5D4F4', enabled: true },
  ],
  colorOverrides: [
    { id: 'override-1', text: 'Override 1', color: '#B5EAD7', enabled: true },
    { id: 'override-2', text: 'Override 2', color: '#AFA9EC', enabled: true },
    { id: 'override-3', text: 'Override 3', color: '#85B7EB', enabled: true },
  ],
  utilisationTiers: [
    { id: 'tier-1', label: 'Available',     color: '#bfdbfe', minPercentage: 0,   maxPercentage: 69,  enabled: true },
    { id: 'tier-2', label: 'On Track',      color: '#bbf7d0', minPercentage: 70,  maxPercentage: 84,  enabled: true },
    { id: 'tier-3', label: 'Near Capacity', color: '#fde68a', minPercentage: 85,  maxPercentage: 100, enabled: true },
    // Overloaded starts at 101% — a resource booked at exactly 100% is at capacity, not overloaded.
    { id: 'tier-4', label: 'Overloaded',    color: '#fecaca', minPercentage: 101, maxPercentage: 999, enabled: true },
  ],
  // Forecast views (Month/Quarter/Year): planned-only cells at or above this % get the corner triangle.
  // Below this threshold, the lighter opacity + dashed border treatment alone is used.
  plannedOnlyWarningThreshold: 70, // kept for backwards compat — forecastSettings takes precedence
  forecastSettings: {
    monthThreshold: 70,
    quarterThreshold: 60,
    yearThreshold: 50,
    showPlannedPrefix: true,

    showConfirmedPlannedSplit: true,
  },
  bookingTypes: [
    {
      id: 'bt-1',
      code: 'PROJECT',
      name: 'Project',
      active: true,
      autoUpdateTimesheet: true,
      doNotUpdateTimesheetWithHours: false,
      obeyUserWorkingProfileAndBankHolidays: true,
      autoAdjustBookingForWorkingProfileAndBankHolidays: false,
      addDaysToEndOfBooking: false,
      allowRepeatBookings: true,
      fieldConfigurations: generateLegacyFieldConfigurations(),
      fieldOrder: generateDefaultFieldOrder(),
      tabConfigurations: generateDefaultTabConfigurations(),
      companyId: 'company-1',
    },
    {
      id: 'bt-2',
      code: 'ABSENCE',
      name: 'Absence',
      active: true,
      autoUpdateTimesheet: false,
      doNotUpdateTimesheetWithHours: false,
      obeyUserWorkingProfileAndBankHolidays: false,
      autoAdjustBookingForWorkingProfileAndBankHolidays: false,
      addDaysToEndOfBooking: true,
      allowRepeatBookings: false,
      fieldConfigurations: generateLegacyFieldConfigurations(),
      fieldOrder: generateDefaultFieldOrder(),
      tabConfigurations: [
        { tabName: 'address', defaultName: 'Address', customName: '', isVisible: false },
        { tabName: 'history', defaultName: 'History', customName: '', isVisible: true },
        { tabName: 'customer-notes', defaultName: 'Customer Notes', customName: '', isVisible: false },
        { tabName: 'project-notes', defaultName: 'Project Notes', customName: '', isVisible: false },
        { tabName: 'custom', defaultName: 'Custom', customName: '', isVisible: false },
      ],
      companyId: 'company-2',
    },
    {
      id: 'bt-3',
      code: 'TRAINING',
      name: 'Training',
      active: true,
      autoUpdateTimesheet: true,
      doNotUpdateTimesheetWithHours: true,
      obeyUserWorkingProfileAndBankHolidays: true,
      autoAdjustBookingForWorkingProfileAndBankHolidays: true,
      addDaysToEndOfBooking: false,
      allowRepeatBookings: true,
      fieldConfigurations: generateLegacyFieldConfigurations(),
      fieldOrder: generateDefaultFieldOrder(),
      tabConfigurations: [
        { tabName: 'address', defaultName: 'Address', customName: 'Training Location', isVisible: true },
        { tabName: 'history', defaultName: 'History', customName: '', isVisible: true },
        { tabName: 'customer-notes', defaultName: 'Customer Notes', customName: 'Attendee Notes', isVisible: true },
        { tabName: 'project-notes', defaultName: 'Project Notes', customName: 'Training Notes', isVisible: true },
        { tabName: 'custom', defaultName: 'Custom', customName: '', isVisible: false },
      ],
      companyId: 'company-3',
    },
    {
      id: 'bt-4',
      code: 'MEETING',
      name: 'Meeting',
      active: true,
      autoUpdateTimesheet: true,
      doNotUpdateTimesheetWithHours: false,
      obeyUserWorkingProfileAndBankHolidays: false,
      autoAdjustBookingForWorkingProfileAndBankHolidays: false,
      addDaysToEndOfBooking: false,
      allowRepeatBookings: true,
      fieldConfigurations: generateLegacyFieldConfigurations(),
      fieldOrder: generateDefaultFieldOrder(),
      tabConfigurations: [
        { tabName: 'address', defaultName: 'Address', customName: 'Meeting Room', isVisible: true },
        { tabName: 'history', defaultName: 'History', customName: '', isVisible: false },
        { tabName: 'customer-notes', defaultName: 'Customer Notes', customName: 'Agenda', isVisible: true },
        { tabName: 'project-notes', defaultName: 'Project Notes', customName: 'Meeting Notes', isVisible: true },
        { tabName: 'custom', defaultName: 'Custom', customName: '', isVisible: false },
      ],
      companyId: 'company-4',
    },
    {
      id: 'bt-5',
      code: 'INTERNAL',
      name: 'Internal Work',
      active: true,
      autoUpdateTimesheet: true,
      doNotUpdateTimesheetWithHours: false,
      obeyUserWorkingProfileAndBankHolidays: true,
      autoAdjustBookingForWorkingProfileAndBankHolidays: false,
      addDaysToEndOfBooking: false,
      allowRepeatBookings: true,
      fieldConfigurations: generateLegacyFieldConfigurations(),
      fieldOrder: generateDefaultFieldOrder(),
      tabConfigurations: generateDefaultTabConfigurations(),
      companyId: 'company-5',
    },
  ],
  defaultBookingTypeId: 'bt-1', // MODIFICATION: Set default booking type to 'Project'
  resourcePermissions: [], // Initialize with empty permissions array
  // Timeline Options
  timelineDefaultTimeRange: 'Month',
  timelineDefaultBookingType: 'bt-1', // Use booking type ID instead of hardcoded string
  timelineDefaultBookingStatus: 'planned',
  timelineDefaultView: 'resource',
  timelineDefaultTimezone: 'Europe/London',
  calendarSyncUrl: '',
  // User Preferences (per-user overrides, undefined means use global default)
  userConfirmDragDrop: undefined,
  userDefaultToLoggedInResource: undefined,
  // Absence Integration
  absenceIntegrationEnabled: false,
  absenceApiUrl: '',
  absenceApiKey: '',
  absenceSyncInterval: 60, // 60 minutes default
  absenceAutoSync: false,
  companyId: 'company-1',
};

// Per-company settings overrides — in live mode appSettingsApi.getAll() returns
// company-specific settings via the X-Company-Id header. Mirror this in mock
// mode so each company can have its own configuration.
export const MOCK_COMPANY_SETTINGS: Record<string, AppSettings> = {
  'company-1': defaultSettings,
  'company-2': {
    ...defaultSettings,
    companyId: 'company-2',
    defaultCalendarView: 'week',
    timelineDefaultTimeRange: 'Month',
    groupingMethod: 'department',
    defaultToLoggedInResource: true,
    quickAddDefault: 'bt-2',
    defaultBookingTypeId: 'bt-2',
    bookingTypes: defaultSettings.bookingTypes.filter(bt => bt.companyId === 'company-2'),
  },
  'company-3': {
    ...defaultSettings,
    companyId: 'company-3',
    defaultCalendarView: 'week',
    timelineDefaultTimeRange: 'Week',
    confirmDragDrop: true,
    enableTimezones: true,
    availableTimezones: ['Europe/London', 'America/New_York', 'Asia/Tokyo'],
    quickAddDefault: 'bt-3',
    defaultBookingTypeId: 'bt-3',
    bookingTypes: defaultSettings.bookingTypes.filter(bt => bt.companyId === 'company-3'),
  },
  'company-4': {
    ...defaultSettings,
    companyId: 'company-4',
    defaultCalendarView: 'day',
    timelineDefaultTimeRange: 'Day',
    businessHoursStart: 9,
    businessHoursEnd: 17,
    visibleHoursStart: 8,
    visibleHoursEnd: 18,
    quickAddDefault: 'bt-4',
    defaultBookingTypeId: 'bt-4',
    bookingTypes: defaultSettings.bookingTypes.filter(bt => bt.companyId === 'company-4'),
  },
  'company-5': {
    ...defaultSettings,
    companyId: 'company-5',
    defaultCalendarView: 'month',
    timelineDefaultTimeRange: 'Month',
    showAbsence: false,
    showPublicHolidays: false,
    quickAddDefault: 'bt-5',
    defaultBookingTypeId: 'bt-5',
    bookingTypes: defaultSettings.bookingTypes.filter(bt => bt.companyId === 'company-5'),
  },
};

// Export mockBookingTypes for use in SchedulerContext
export const mockBookingTypes = defaultSettings.bookingTypes;

// Mock Clients with UK Addresses
export const mockClients: Client[] = [
  { 
    id: 'c1', 
    name: 'Acme Corporation', 
    code: 'ACME-001',
    industry: 'Technology',
    streetAddress: '45 Broadwick Street',
    addressLine2: 'Floor 3, Soho Works',
    city: 'London',
    county: 'Greater London',
    postcode: 'W1F 9QZ',
    country: 'United Kingdom',
    mobileNumber: '07700 900123',
    phoneNumber: '020 7946 0958',
    email: 'info@acmecorp.co.uk',
    companyId: 'company-1',
  },
  { 
    id: 'c2', 
    name: 'Global Finance Ltd', 
    code: 'GFL-002',
    industry: 'Financial Services',
    streetAddress: '123 Deansgate',
    addressLine2: 'Suite 12, Deansgate Square',
    city: 'Manchester',
    county: 'Greater Manchester',
    postcode: 'M3 2BW',
    country: 'United Kingdom',
    mobileNumber: '07700 900456',
    phoneNumber: '0161 496 0123',
    email: 'contact@globalfinance.co.uk',
    companyId: 'company-2',
  },
  { 
    id: 'c3', 
    name: 'HealthTech Solutions', 
    code: 'HTS-003',
    industry: 'Healthcare',
    streetAddress: '78 George Street',
    city: 'Edinburgh',
    county: 'Midlothian',
    postcode: 'EH2 3BU',
    country: 'United Kingdom',
    mobileNumber: '07700 900789',
    phoneNumber: '0131 496 0234',
    email: 'enquiries@healthtechsolutions.co.uk',
    companyId: 'company-3',
  },
  { 
    id: 'c4', 
    name: 'Retail Innovations Inc', 
    code: 'RII-004',
    industry: 'Retail',
    streetAddress: '56 Park Street',
    city: 'Bristol',
    county: 'Bristol',
    postcode: 'BS1 5JN',
    country: 'United Kingdom',
    mobileNumber: '07700 900321',
    phoneNumber: '0117 496 0345',
    email: 'hello@retailinnovations.co.uk',
    companyId: 'company-3',
  },
  { 
    id: 'c5', 
    name: 'EduLearn Platform', 
    code: 'ELP-005',
    industry: 'Education',
    streetAddress: '92 Bold Street',
    city: 'Liverpool',
    county: 'Merseyside',
    postcode: 'L1 4HY',
    country: 'United Kingdom',
    mobileNumber: '07700 900654',
    phoneNumber: '0151 496 0456',
    email: 'support@edulearn.co.uk',
    companyId: 'company-4',
  },
  { 
    id: 'c6', 
    name: 'GreenEnergy Co', 
    code: 'GEC-006',
    industry: 'Energy',
    streetAddress: '34 Corporation Street',
    city: 'Birmingham',
    county: 'West Midlands',
    postcode: 'B2 4LP',
    country: 'United Kingdom',
    mobileNumber: '07700 900987',
    phoneNumber: '0121 496 0567',
    email: 'info@greenenergy.co.uk',
    companyId: 'company-4',
  },
  { 
    id: 'c7', 
    name: 'Internal', 
    code: 'INT-007',
    industry: 'Internal',
    streetAddress: '10 Downing Street',
    city: 'London',
    county: 'Greater London',
    postcode: 'SW1A 2AA',
    country: 'United Kingdom',
    mobileNumber: '07700 900111',
    phoneNumber: '020 7946 0100',
    email: 'admin@internal.co.uk',
    companyId: 'company-5',
  },
];

// Mock Cost Centres
// Cost Centres — like live mode (EBProjects), every cost centre belongs to exactly one
// project and is matched to it by `projectCode` → the project's `billingCode`.
export const mockCostCentres: CostCentre[] = [
  // Website Redesign (ACM-2024-001)
  { id: 'cc1', name: 'Engineering - Frontend', code: 'ACM-2024-001-ENG-FE', description: 'Frontend development and UI engineering', department: 'Technology', status: 'in progress', projectCode: 'ACM-2024-001', userSubtotal: 'US-ENG-FE', companyId: 'company-1' },
  { id: 'cc2', name: 'Engineering - Backend', code: 'ACM-2024-001-ENG-BE', description: 'Backend services and API development', department: 'Technology', status: 'in progress', projectCode: 'ACM-2024-001', userSubtotal: 'US-ENG-BE', companyId: 'company-1' },
  { id: 'cc3', name: 'Design & UX', code: 'ACM-2024-001-DES', description: 'User experience design and visual design', department: 'Creative', status: 'in progress', projectCode: 'ACM-2024-001', userSubtotal: 'US-DES', companyId: 'company-1' },
  { id: 'cc16', name: 'Project Management', code: 'ACM-2024-001-PM', description: 'Project planning, tracking and delivery', department: 'Operations', status: 'in progress', projectCode: 'ACM-2024-001', userSubtotal: 'US-PM', companyId: 'company-1' },
  // Mobile App Design (ACM-2024-002)
  { id: 'cc17', name: 'Engineering - Frontend', code: 'ACM-2024-002-ENG-FE', description: 'Frontend development and UI engineering', department: 'Technology', status: 'in progress', projectCode: 'ACM-2024-002', userSubtotal: 'US-ENG-FE', companyId: 'company-1' },
  { id: 'cc18', name: 'Design & UX', code: 'ACM-2024-002-DES', description: 'User experience design and visual design', department: 'Creative', status: 'in progress', projectCode: 'ACM-2024-002', userSubtotal: 'US-DES', companyId: 'company-1' },
  { id: 'cc19', name: 'Quality Assurance', code: 'ACM-2024-002-QA', description: 'Testing and quality control', department: 'Technology', status: 'in progress', projectCode: 'ACM-2024-002', userSubtotal: 'US-QA', companyId: 'company-1' },
  { id: 'cc20', name: 'Project Management', code: 'ACM-2024-002-PM', description: 'Project planning, tracking and delivery', department: 'Operations', status: 'in progress', projectCode: 'ACM-2024-002', userSubtotal: 'US-PM', companyId: 'company-1' },
  // API Integration (GFL-2024-015)
  { id: 'cc21', name: 'Engineering - Backend', code: 'GFL-2024-015-ENG-BE', description: 'Backend services and API development', department: 'Technology', status: 'in progress', projectCode: 'GFL-2024-015', userSubtotal: 'UK-ENG-BE', companyId: 'company-2' },
  { id: 'cc5', name: 'Quality Assurance', code: 'GFL-2024-015-QA', description: 'Testing and quality control', department: 'Technology', status: 'in progress', projectCode: 'GFL-2024-015', userSubtotal: 'UK-QA', companyId: 'company-2' },
  { id: 'cc22', name: 'DevOps & Infrastructure', code: 'GFL-2024-015-OPS', description: 'Infrastructure management and CI/CD', department: 'Infrastructure', status: 'in progress', projectCode: 'GFL-2024-015', userSubtotal: 'UK-OPS', companyId: 'company-2' },
  { id: 'cc23', name: 'Project Management', code: 'GFL-2024-015-PM', description: 'Project planning, tracking and delivery', department: 'Operations', status: 'in progress', projectCode: 'GFL-2024-015', userSubtotal: 'UK-PM', companyId: 'company-2' },
  // Infrastructure Setup (GFL-2024-016)
  { id: 'cc24', name: 'Engineering - Backend', code: 'GFL-2024-016-ENG-BE', description: 'Backend services and API development', department: 'Technology', status: 'in progress', projectCode: 'GFL-2024-016', userSubtotal: 'UK-ENG-BE', companyId: 'company-2' },
  { id: 'cc6', name: 'DevOps & Infrastructure', code: 'GFL-2024-016-OPS', description: 'Infrastructure management and CI/CD', department: 'Infrastructure', status: 'in progress', projectCode: 'GFL-2024-016', userSubtotal: 'UK-OPS', companyId: 'company-2' },
  { id: 'cc25', name: 'Security & Compliance', code: 'GFL-2024-016-SEC', description: 'Information security and regulatory compliance', department: 'Technology', status: 'in progress', projectCode: 'GFL-2024-016', userSubtotal: 'UK-SEC', companyId: 'company-2' },
  { id: 'cc4', name: 'Project Management', code: 'GFL-2024-016-PM', description: 'Project planning, tracking and delivery', department: 'Operations', status: 'in progress', projectCode: 'GFL-2024-016', userSubtotal: 'UK-PM', companyId: 'company-2' },
  // Testing Phase 2 (HTS-2024-008)
  { id: 'cc26', name: 'Quality Assurance', code: 'HTS-2024-008-QA', description: 'Testing and quality control', department: 'Technology', status: 'in progress', projectCode: 'HTS-2024-008', userSubtotal: 'US-QA', companyId: 'company-3' },
  { id: 'cc7', name: 'Business Analysis', code: 'HTS-2024-008-BA', description: 'Requirements gathering and business process analysis', department: 'Strategy', status: 'in progress', projectCode: 'HTS-2024-008', userSubtotal: 'US-BA', companyId: 'company-3' },
  { id: 'cc8', name: 'Marketing & Communications', code: 'HTS-2024-008-MKT', description: 'Marketing campaigns and corporate communications', department: 'Marketing', status: 'in progress', projectCode: 'HTS-2024-008', userSubtotal: 'US-MKT', companyId: 'company-3' },
  { id: 'cc9', name: 'Research & Development', code: 'HTS-2024-008-RND', description: 'Innovation and new technology research', department: 'Innovation', status: 'in progress', projectCode: 'HTS-2024-008', userSubtotal: 'US-RND', companyId: 'company-3' },
  { id: 'cc27', name: 'Project Management', code: 'HTS-2024-008-PM', description: 'Project planning, tracking and delivery', department: 'Operations', status: 'in progress', projectCode: 'HTS-2024-008', userSubtotal: 'US-PM', companyId: 'company-3' },
  // Component Library (RII-2024-022)
  { id: 'cc28', name: 'Engineering - Frontend', code: 'RII-2024-022-ENG-FE', description: 'Frontend development and UI engineering', department: 'Technology', status: 'completed', projectCode: 'RII-2024-022', userSubtotal: 'US-ENG-FE', companyId: 'company-3' },
  { id: 'cc29', name: 'Design & UX', code: 'RII-2024-022-DES', description: 'User experience design and visual design', department: 'Creative', status: 'completed', projectCode: 'RII-2024-022', userSubtotal: 'US-DES', companyId: 'company-3' },
  { id: 'cc30', name: 'Project Management', code: 'RII-2024-022-PM', description: 'Project planning, tracking and delivery', department: 'Operations', status: 'completed', projectCode: 'RII-2024-022', userSubtotal: 'US-PM', companyId: 'company-3' },
  // Brand Guidelines (RII-2024-023)
  { id: 'cc31', name: 'Design & UX', code: 'RII-2024-023-DES', description: 'User experience design and visual design', department: 'Creative', status: 'planning', projectCode: 'RII-2024-023', userSubtotal: 'CA-DES', companyId: 'company-4' },
  { id: 'cc32', name: 'Marketing & Communications', code: 'RII-2024-023-MKT', description: 'Marketing campaigns and corporate communications', department: 'Marketing', status: 'planning', projectCode: 'RII-2024-023', userSubtotal: 'CA-MKT', companyId: 'company-4' },
  { id: 'cc33', name: 'Project Management', code: 'RII-2024-023-PM', description: 'Project planning, tracking and delivery', department: 'Operations', status: 'planning', projectCode: 'RII-2024-023', userSubtotal: 'CA-PM', companyId: 'company-4' },
  // Sprint Planning (ELP-2024-011)
  { id: 'cc34', name: 'Business Analysis', code: 'ELP-2024-011-BA', description: 'Requirements gathering and business process analysis', department: 'Strategy', status: 'in progress', projectCode: 'ELP-2024-011', userSubtotal: 'CA-BA', companyId: 'company-4' },
  { id: 'cc35', name: 'Product Management', code: 'ELP-2024-011-PROD', description: 'Product strategy and roadmap management', department: 'Product', status: 'in progress', projectCode: 'ELP-2024-011', userSubtotal: 'CA-PROD', companyId: 'company-4' },
  { id: 'cc36', name: 'Project Management', code: 'ELP-2024-011-PM', description: 'Project planning, tracking and delivery', department: 'Operations', status: 'in progress', projectCode: 'ELP-2024-011', userSubtotal: 'CA-PM', companyId: 'company-4' },
  // Data Analytics Dashboard (HTS-2024-009)
  { id: 'cc10', name: 'Data Analytics', code: 'HTS-2024-009-DATA', description: 'Data analysis and business intelligence', department: 'Technology', status: 'planning', projectCode: 'HTS-2024-009', userSubtotal: 'UK-DATA', companyId: 'company-5' },
  { id: 'cc37', name: 'Engineering - Backend', code: 'HTS-2024-009-ENG-BE', description: 'Backend services and API development', department: 'Technology', status: 'planning', projectCode: 'HTS-2024-009', userSubtotal: 'UK-ENG-BE', companyId: 'company-5' },
  { id: 'cc38', name: 'Project Management', code: 'HTS-2024-009-PM', description: 'Project planning, tracking and delivery', department: 'Operations', status: 'planning', projectCode: 'HTS-2024-009', userSubtotal: 'UK-PM', companyId: 'company-5' },
  // Cloud Migration (GEC-2024-005)
  { id: 'cc11', name: 'DevOps & Infrastructure', code: 'GEC-2024-005-OPS', description: 'Infrastructure management and CI/CD', department: 'Infrastructure', status: 'on hold', projectCode: 'GEC-2024-005', userSubtotal: 'UK-OPS', companyId: 'company-5' },
  { id: 'cc12', name: 'Security & Compliance', code: 'GEC-2024-005-SEC', description: 'Information security and regulatory compliance', department: 'Technology', status: 'on hold', projectCode: 'GEC-2024-005', userSubtotal: 'UK-SEC', companyId: 'company-5' },
  { id: 'cc39', name: 'Project Management', code: 'GEC-2024-005-PM', description: 'Project planning, tracking and delivery', department: 'Operations', status: 'on hold', projectCode: 'GEC-2024-005', userSubtotal: 'UK-PM', companyId: 'company-5' },
  // Security Audit (GFL-2024-017)
  { id: 'cc13', name: 'Security & Compliance', code: 'GFL-2024-017-SEC', description: 'Information security and regulatory compliance', department: 'Technology', status: 'cancelled', projectCode: 'GFL-2024-017', userSubtotal: 'UK-SEC', companyId: 'company-2' },
  { id: 'cc40', name: 'Business Analysis', code: 'GFL-2024-017-BA', description: 'Requirements gathering and business process analysis', department: 'Strategy', status: 'cancelled', projectCode: 'GFL-2024-017', userSubtotal: 'UK-BA', companyId: 'company-2' },
  { id: 'cc42', name: 'Project Management', code: 'GFL-2024-017-PM', description: 'Project planning, tracking and delivery', department: 'Operations', status: 'cancelled', projectCode: 'GFL-2024-017', userSubtotal: 'UK-PM', companyId: 'company-2' },
  // Internal Training (INT-2024-001)
  { id: 'cc14', name: 'Finance & Administration', code: 'INT-2024-001-FIN', description: 'Financial management and office administration', department: 'Finance', status: 'in progress', projectCode: 'INT-2024-001', userSubtotal: 'UK-FIN', companyId: 'company-5' },
  { id: 'cc15', name: 'Project Management', code: 'INT-2024-001-PM', description: 'Project planning, tracking and delivery', department: 'Operations', status: 'in progress', projectCode: 'INT-2024-001', userSubtotal: 'UK-PM', companyId: 'company-5' },
  { id: 'cc41', name: 'Business Analysis', code: 'INT-2024-001-BA', description: 'Requirements gathering and business process analysis', department: 'Strategy', status: 'in progress', projectCode: 'INT-2024-001', userSubtotal: 'UK-BA', companyId: 'company-5' },
];

/**
 * Project to Cost Centre Mapping
 *
 * Derived view of `mockCostCentres`: which cost centres belong to each project.
 * Mirrors live mode, where a cost centre belongs to a single project — so no cost
 * centre is shared between projects and changing project always clears the selection.
 */
export const projectCostCentreMapping: Record<string, string[]> = {
  // Website Redesign (ACM-2024-001) — Engineering - Frontend, Engineering - Backend, Design & UX, Project Management
  'pd1': ['cc1', 'cc2', 'cc3', 'cc16'],
  // Mobile App Design (ACM-2024-002) — Engineering - Frontend, Design & UX, Quality Assurance, Project Management
  'pd2': ['cc17', 'cc18', 'cc19', 'cc20'],
  // API Integration (GFL-2024-015) — Engineering - Backend, Quality Assurance, DevOps & Infrastructure, Project Management
  'pd3': ['cc21', 'cc5', 'cc22', 'cc23'],
  // Infrastructure Setup (GFL-2024-016) — Engineering - Backend, DevOps & Infrastructure, Security & Compliance, Project Management
  'pd4': ['cc24', 'cc6', 'cc25', 'cc4'],
  // Testing Phase 2 (HTS-2024-008) — Quality Assurance, Business Analysis, Marketing & Communications, Research & Development, Project Management
  'pd5': ['cc26', 'cc7', 'cc8', 'cc9', 'cc27'],
  // Component Library (RII-2024-022) — Engineering - Frontend, Design & UX, Project Management
  'pd6': ['cc28', 'cc29', 'cc30'],
  // Brand Guidelines (RII-2024-023) — Design & UX, Marketing & Communications, Project Management
  'pd7': ['cc31', 'cc32', 'cc33'],
  // Sprint Planning (ELP-2024-011) — Business Analysis, Product Management, Project Management
  'pd8': ['cc34', 'cc35', 'cc36'],
  // Data Analytics Dashboard (HTS-2024-009) — Data Analytics, Engineering - Backend, Project Management
  'pd9': ['cc10', 'cc37', 'cc38'],
  // Cloud Migration (GEC-2024-005) — DevOps & Infrastructure, Security & Compliance, Project Management
  'pd10': ['cc11', 'cc12', 'cc39'],
  // Security Audit (GFL-2024-017) — Security & Compliance, Business Analysis, Project Management
  'pd11': ['cc13', 'cc40', 'cc42'],
  // Internal Training (INT-2024-001) — Finance & Administration, Project Management, Business Analysis
  'pd12': ['cc14', 'cc15', 'cc41'],
};

/**
 * Helper function to get cost centres for a specific project
 * @param projectId - The project ID to get cost centres for
 * @returns Array of cost centre IDs associated with the project, or empty array if no mapping exists
 */
export function getCostCentresForProject(projectId: string): string[] {
  return projectCostCentreMapping[projectId] || [];
}

/**
 * Helper function to check if a cost centre is associated with a project
 * @param projectId - The project ID to check
 * @param costCentreId - The cost centre ID to check
 * @returns true if the cost centre is associated with the project
 */
export function isCostCentreInProject(projectId: string, costCentreId: string): boolean {
  const costCentres = projectCostCentreMapping[projectId];
  return costCentres ? costCentres.includes(costCentreId) : false;
}

/**
 * Helper function to get the customer (client) ID for a specific project
 * @param projectId - The project ID to get the customer for
 * @returns The client ID associated with the project, or undefined if not found
 */
export function getCustomerForProject(projectId: string): string | undefined {
  const project = mockProjectDetails.find(p => String(p.id) === String(projectId));
  return project?.clientId != null ? String(project.clientId) : undefined;
}

/**
 * Helper function to get projects for a specific customer (client)
 * @param customerId - The customer/client ID to get projects for
 * @returns Array of project IDs associated with the customer
 */
export function getProjectsForCustomer(customerId: string): string[] {
  return mockProjectDetails
    .filter(p => String(p.clientId) === String(customerId))
    .map(p => String(p.id));
}

/**
 * Helper function to check if a project belongs to a specific customer
 * @param projectId - The project ID to check
 * @param customerId - The customer/client ID to check
 * @returns true if the project belongs to the customer
 */
export function isProjectForCustomer(projectId: string, customerId: string): boolean {
  const project = mockProjectDetails.find(p => String(p.id) === String(projectId));
  return String(project?.clientId) === String(customerId);
}

// Mock Absences - various types for different resources across 8 weeks
// Reusing the 'today' variable declared at the top of the file

// Helper function to get date offset from today
const getDateOffset = (days: number): string => {
  const date = new Date(today);
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
};

export const mockAbsences: Absence[] = [];

/* TEMPORARILY DISABLED - Absence data removed to prevent ID collision errors
export const mockAbsences_DISABLED: Absence[] = [
  // ===== WEEK 1 (Days 0-6) =====
  // Today - David Kim full day annual leave
  {
    id: 'abs-1',
    resourceId: '4',
    type: 'full-day',
    reason: 'Annual Leave',
    startDate: getDateOffset(0),
    endDate: getDateOffset(0),
  },
  // Today - Emily Rodriguez hourly dentist appointment
  {
    id: 'abs-2',
    resourceId: '3',
    type: 'hourly',
    reason: 'Dentist Appointment',
    startDate: getDateOffset(0),
    endDate: getDateOffset(0),
    startTime: '14:00',
    endTime: '16:00',
  },
  // Tomorrow - Sarah Johnson half-day AM medical
  {
    id: 'abs-3',
    resourceId: '1',
    type: 'half-day-am',
    reason: 'Medical Appointment',
    startDate: getDateOffset(1),
    endDate: getDateOffset(1),
  },
  // Day 2 - Michael Chen half-day PM personal
  {
    id: 'abs-4',
    resourceId: '2',
    type: 'half-day-pm',
    reason: 'Personal',
    startDate: getDateOffset(2),
    endDate: getDateOffset(2),
  },
  // Day 3 - Robert Taylor hourly school run
  {
    id: 'abs-5',
    resourceId: '6',
    type: 'hourly',
    reason: 'School Run',
    startDate: getDateOffset(3),
    endDate: getDateOffset(3),
    startTime: '08:00',
    endTime: '09:30',
  },
  // Day 4-5 - Amanda Martinez 2-day sick leave
  {
    id: 'abs-6',
    resourceId: '7',
    type: 'full-day',
    reason: 'Sick Leave',
    startDate: getDateOffset(4),
    endDate: getDateOffset(5),
  },

  // ===== WEEK 2 (Days 7-13) =====
  // Day 7 - Jessica Williams full day annual leave
  {
    id: 'abs-7',
    resourceId: '5',
    type: 'full-day',
    reason: 'Annual Leave',
    startDate: getDateOffset(7),
    endDate: getDateOffset(7),
  },
  // Day 8 - James Anderson half-day AM training
  {
    id: 'abs-8',
    resourceId: '8',
    type: 'half-day-am',
    reason: 'Training',
    startDate: getDateOffset(8),
    endDate: getDateOffset(8),
  },
  // Day 9 - Sarah Johnson hourly doctor appointment
  {
    id: 'abs-9',
    resourceId: '1',
    type: 'hourly',
    reason: 'Doctor Appointment',
    startDate: getDateOffset(9),
    endDate: getDateOffset(9),
    startTime: '10:00',
    endTime: '11:30',
  },
  // Day 10-12 - David Kim 3-day holiday
  {
    id: 'abs-10',
    resourceId: '4',
    type: 'full-day',
    reason: 'Holiday',
    startDate: getDateOffset(10),
    endDate: getDateOffset(12),
  },
  // Day 13 - Emily Rodriguez half-day PM personal
  {
    id: 'abs-11',
    resourceId: '3',
    type: 'half-day-pm',
    reason: 'Personal',
    startDate: getDateOffset(13),
    endDate: getDateOffset(13),
  },

  // ===== WEEK 3 (Days 14-20) =====
  // Day 14 - Michael Chen full day sick leave
  {
    id: 'abs-12',
    resourceId: '2',
    type: 'full-day',
    reason: 'Sick Leave',
    startDate: getDateOffset(14),
    endDate: getDateOffset(14),
  },
  // Day 15 - Robert Taylor hourly car service
  {
    id: 'abs-13',
    resourceId: '6',
    type: 'hourly',
    reason: 'Car Service',
    startDate: getDateOffset(15),
    endDate: getDateOffset(15),
    startTime: '13:00',
    endTime: '15:00',
  },
  // Day 16-17 - Amanda Martinez 2-day annual leave
  {
    id: 'abs-14',
    resourceId: '7',
    type: 'full-day',
    reason: 'Annual Leave',
    startDate: getDateOffset(16),
    endDate: getDateOffset(17),
  },
  // Day 18 - Jessica Williams half-day AM medical
  {
    id: 'abs-15',
    resourceId: '5',
    type: 'half-day-am',
    reason: 'Medical Appointment',
    startDate: getDateOffset(18),
    endDate: getDateOffset(18),
  },
  // Day 19 - James Anderson hourly bank appointment
  {
    id: 'abs-16',
    resourceId: '8',
    type: 'hourly',
    reason: 'Bank Appointment',
    startDate: getDateOffset(19),
    endDate: getDateOffset(19),
    startTime: '09:00',
    endTime: '10:00',
  },

  // ===== WEEK 4 (Days 21-27) =====
  // Day 21 - Sarah Johnson full day annual leave
  {
    id: 'abs-17',
    resourceId: '1',
    type: 'full-day',
    reason: 'Annual Leave',
    startDate: getDateOffset(21),
    endDate: getDateOffset(21),
  },
  // Day 22-24 - Michael Chen 3-day holiday
  {
    id: 'abs-18',
    resourceId: '2',
    type: 'full-day',
    reason: 'Holiday',
    startDate: getDateOffset(22),
    endDate: getDateOffset(24),
  },
  // Day 25 - Emily Rodriguez hourly physio
  {
    id: 'abs-19',
    resourceId: '3',
    type: 'hourly',
    reason: 'Physiotherapy',
    startDate: getDateOffset(25),
    endDate: getDateOffset(25),
    startTime: '15:00',
    endTime: '16:30',
  },
  // Day 26 - David Kim half-day PM personal
  {
    id: 'abs-20',
    resourceId: '4',
    type: 'half-day-pm',
    reason: 'Personal',
    startDate: getDateOffset(26),
    endDate: getDateOffset(26),
  },
  // Day 27 - Robert Taylor full day sick leave
  {
    id: 'abs-21',
    resourceId: '6',
    type: 'full-day',
    reason: 'Sick Leave',
    startDate: getDateOffset(27),
    endDate: getDateOffset(27),
  },

  // ===== WEEK 5 (Days 28-34) =====
  // Day 28-29 - Jessica Williams 2-day annual leave
  {
    id: 'abs-22',
    resourceId: '5',
    type: 'full-day',
    reason: 'Annual Leave',
    startDate: getDateOffset(28),
    endDate: getDateOffset(29),
  },
  // Day 30 - Amanda Martinez half-day AM training
  {
    id: 'abs-23',
    resourceId: '7',
    type: 'half-day-am',
    reason: 'Training',
    startDate: getDateOffset(30),
    endDate: getDateOffset(30),
  },
  // Day 31 - James Anderson hourly dentist
  {
    id: 'abs-24',
    resourceId: '8',
    type: 'hourly',
    reason: 'Dentist Appointment',
    startDate: getDateOffset(31),
    endDate: getDateOffset(31),
    startTime: '11:00',
    endTime: '12:00',
  },
  // Day 32 - Sarah Johnson half-day PM personal
  {
    id: 'abs-25',
    resourceId: '1',
    type: 'half-day-pm',
    reason: 'Personal',
    startDate: getDateOffset(32),
    endDate: getDateOffset(32),
  },
  // Day 33-34 - Michael Chen 2-day sick leave
  {
    id: 'abs-26',
    resourceId: '2',
    type: 'full-day',
    reason: 'Sick Leave',
    startDate: getDateOffset(33),
    endDate: getDateOffset(34),
  },

  // ===== WEEK 6 (Days 35-41) =====
  // Day 35 - Emily Rodriguez full day annual leave
  {
    id: 'abs-27',
    resourceId: '3',
    type: 'full-day',
    reason: 'Annual Leave',
    startDate: getDateOffset(35),
    endDate: getDateOffset(35),
  },
  // Day 36 - David Kim hourly optician
  {
    id: 'abs-28',
    resourceId: '4',
    type: 'hourly',
    reason: 'Optician Appointment',
    startDate: getDateOffset(36),
    endDate: getDateOffset(36),
    startTime: '14:00',
    endTime: '15:30',
  },
  // Day 37-39 - Robert Taylor 3-day holiday
  {
    id: 'abs-29',
    resourceId: '6',
    type: 'full-day',
    reason: 'Holiday',
    startDate: getDateOffset(37),
    endDate: getDateOffset(39),
  },
  // Day 40 - Amanda Martinez half-day AM medical
  {
    id: 'abs-30',
    resourceId: '7',
    type: 'half-day-am',
    reason: 'Medical Appointment',
    startDate: getDateOffset(40),
    endDate: getDateOffset(40),
  },
  // Day 41 - Jessica Williams hourly parent meeting
  {
    id: 'abs-31',
    resourceId: '5',
    type: 'hourly',
    reason: 'Parent Meeting',
    startDate: getDateOffset(41),
    endDate: getDateOffset(41),
    startTime: '16:00',
    endTime: '17:00',
  },

  // ===== WEEK 7 (Days 42-48) =====
  // Day 42 - James Anderson full day annual leave
  {
    id: 'abs-32',
    resourceId: '8',
    type: 'full-day',
    reason: 'Annual Leave',
    startDate: getDateOffset(42),
    endDate: getDateOffset(42),
  },
  // Day 43 - Sarah Johnson hourly solicitor
  {
    id: 'abs-33',
    resourceId: '1',
    type: 'hourly',
    reason: 'Solicitor Appointment',
    startDate: getDateOffset(43),
    endDate: getDateOffset(43),
    startTime: '10:30',
    endTime: '12:00',
  },
  // Day 44-45 - Michael Chen 2-day annual leave
  {
    id: 'abs-34',
    resourceId: '2',
    type: 'full-day',
    reason: 'Annual Leave',
    startDate: getDateOffset(44),
    endDate: getDateOffset(45),
  },
  // Day 46 - Emily Rodriguez half-day PM personal
  {
    id: 'abs-35',
    resourceId: '3',
    type: 'half-day-pm',
    reason: 'Personal',
    startDate: getDateOffset(46),
    endDate: getDateOffset(46),
  },
  // Day 47 - David Kim full day sick leave
  {
    id: 'abs-36',
    resourceId: '4',
    type: 'full-day',
    reason: 'Sick Leave',
    startDate: getDateOffset(47),
    endDate: getDateOffset(47),
  },

  // ===== WEEK 8 (Days 49-55) =====
  // Day 49 - Robert Taylor half-day AM training
  {
    id: 'abs-37',
    resourceId: '6',
    type: 'half-day-am',
    reason: 'Training',
    startDate: getDateOffset(49),
    endDate: getDateOffset(49),
  },
  // Day 50-52 - Amanda Martinez 3-day holiday
  {
    id: 'abs-38',
    resourceId: '7',
    type: 'full-day',
    reason: 'Holiday',
    startDate: getDateOffset(50),
    endDate: getDateOffset(52),
  },
  // Day 53 - Jessica Williams hourly vet appointment
  {
    id: 'abs-39',
    resourceId: '5',
    type: 'hourly',
    reason: 'Vet Appointment',
    startDate: getDateOffset(53),
    endDate: getDateOffset(53),
    startTime: '13:30',
    endTime: '14:30',
  },
  // Day 54 - James Anderson half-day PM personal
  {
    id: 'abs-40',
    resourceId: '8',
    type: 'half-day-pm',
    reason: 'Personal',
    startDate: getDateOffset(54),
    endDate: getDateOffset(54),
  },
  // Day 55 - Sarah Johnson full day annual leave
  {
    id: 'abs-41',
    resourceId: '1',
    type: 'full-day',
    reason: 'Annual Leave',
    startDate: getDateOffset(55),
    endDate: getDateOffset(55),
  },
];
*/

// Mock Bank Holidays - spread across the 8-week period and beyond
export const mockBankHolidays: BankHoliday[] = [];

/* TEMPORARILY DISABLED - Bank holiday data removed to prevent ID collision errors
export const mockBankHolidays_DISABLED: BankHoliday[] = [
  // Current year bank holidays (UK) - using dynamic dates relative to today
  { id: 'bh-1', name: "Team Building Day", date: getDateOffset(5), country: 'GB' },
  { id: 'bh-2', name: 'Company Anniversary', date: getDateOffset(14), country: 'GB' },
  { id: 'bh-3', name: 'Regional Holiday', date: getDateOffset(28), country: 'GB' },
  { id: 'bh-4', name: 'National Day', date: getDateOffset(42), country: 'GB' },
  
  // Fixed UK bank holidays for 2026
  { id: 'bh-5', name: "New Year's Day", date: '2026-01-01', country: 'GB' },
  { id: 'bh-6', name: 'Good Friday', date: '2026-04-03', country: 'GB' },
  { id: 'bh-7', name: 'Easter Monday', date: '2026-04-06', country: 'GB' },
  { id: 'bh-8', name: 'Early May Bank Holiday', date: '2026-05-04', country: 'GB' },
  { id: 'bh-9', name: 'Spring Bank Holiday', date: '2026-05-25', country: 'GB' },
  { id: 'bh-10', name: 'Summer Bank Holiday', date: '2026-08-31', country: 'GB' },
  { id: 'bh-11', name: 'Christmas Day', date: '2026-12-25', country: 'GB' },
  { id: 'bh-12', name: 'Boxing Day', date: '2026-12-28', country: 'GB' },
];
*/

// Helper: resolve mock cost centres for a project using the projectCostCentreMapping
const getMockProjectCostCentres = (projectId: string): CostCentre[] => {
  const ccIds = projectCostCentreMapping[projectId] || [];
  return mockCostCentres.filter(cc => ccIds.includes(String(cc.id)));
};

// Mock Project Details
export const mockProjectDetails: ProjectDetails[] = [
  {
    id: 'pd1',
    name: 'Website Redesign',
    clientId: 'c1',
    clientName: 'Acme Corporation',
    billingCode: 'ACM-2024-001',
    projectManager: 'Emily Rodriguez',
    color: getProjectColor('Website Redesign'),
    status: 'in progress',
    description: 'Complete redesign of company website with modern UI/UX',
    costCentres: getMockProjectCostCentres('pd1'),
    sortKey: 'ACM-001',
    userSort1: 'USA',
    userSort2: 'North America',
    userSort3: 'Enterprise',
    userChar1: 'Active',
    userChar2: 'High Priority',
    companyId: 'company-1',
  },
  {
    id: 'pd2',
    name: 'Mobile App Design',
    clientId: 'c1',
    clientName: 'Acme Corporation',
    billingCode: 'ACM-2024-002',
    projectManager: 'Emily Rodriguez',
    color: getProjectColor('Mobile App Design'),
    status: 'in progress',
    description: 'Design and prototype for iOS and Android mobile application',
    costCentres: getMockProjectCostCentres('pd2'),
    sortKey: 'ACM-002',
    userSort1: 'USA',
    userSort2: 'North America',
    userSort3: 'Mobile',
    userChar1: 'Active',
    userChar2: 'Medium Priority',
    companyId: 'company-1',
  },
  {
    id: 'pd3',
    name: 'API Integration',
    clientId: 'c2',
    clientName: 'Global Finance Ltd',
    billingCode: 'GFL-2024-015',
    projectManager: 'Michael Chen',
    color: getProjectColor('API Integration'),
    status: 'in progress',
    description: 'Integration with third-party payment and authentication APIs',
    costCentres: getMockProjectCostCentres('pd3'),
    sortKey: 'GFL-015',
    userSort1: 'UK',
    userSort2: 'Europe',
    userSort3: 'Integration',
    userChar1: 'Active',
    userChar2: 'High Priority',
    companyId: 'company-2',
  },
  {
    id: 'pd4',
    name: 'Infrastructure Setup',
    clientId: 'c2',
    clientName: 'Global Finance Ltd',
    billingCode: 'GFL-2024-016',
    projectManager: 'Robert Taylor',
    color: getProjectColor('Infrastructure Setup'),
    status: 'in progress',
    description: 'Cloud infrastructure setup and deployment automation',
    costCentres: getMockProjectCostCentres('pd4'),
    sortKey: 'GFL-016',
    userSort1: 'UK',
    userSort2: 'Europe',
    userSort3: 'Infrastructure',
    userChar1: 'Reopened',
    userChar2: 'Critical',
    companyId: 'company-2',
  },
  {
    id: 'pd5',
    name: 'Testing Phase 2',
    clientId: 'c3',
    clientName: 'HealthTech Solutions',
    billingCode: 'HTS-2024-008',
    projectManager: 'Jessica Williams',
    color: getProjectColor('Testing Phase 2'),
    status: 'in progress',
    description: 'Comprehensive testing of all system components and integrations',
    costCentres: getMockProjectCostCentres('pd5'),
    sortKey: 'HTS-008',
    userSort1: 'Canada',
    userSort2: 'North America',
    userSort3: 'Testing',
    userChar1: 'Active',
    userChar2: 'Medium Priority',
    companyId: 'company-3',
  },
  {
    id: 'pd6',
    name: 'Component Library',
    clientId: 'c4',
    clientName: 'Retail Innovations Inc',
    billingCode: 'RII-2024-022',
    projectManager: 'Amanda Martinez',
    color: getProjectColor('Component Library'),
    status: 'completed',
    costCentres: getMockProjectCostCentres('pd6'),
    sortKey: 'RII-022',
    userSort1: 'Germany',
    userSort2: 'Europe',
    userSort3: 'Development',
    userChar1: 'Completed',
    userChar2: 'Low Priority',
    companyId: 'company-3',
  },
  {
    id: 'pd7',
    name: 'Brand Guidelines',
    clientId: 'c4',
    clientName: 'Retail Innovations Inc',
    billingCode: 'RII-2024-023',
    projectManager: 'Michael Chen',
    color: getProjectColor('Brand Guidelines'),
    status: 'planning',
    costCentres: getMockProjectCostCentres('pd7'),
    companyId: 'company-4',
  },
  {
    id: 'pd8',
    name: 'Sprint Planning',
    clientId: 'c5',
    clientName: 'EduLearn Platform',
    billingCode: 'ELP-2024-011',
    projectManager: 'Emily Rodriguez',
    color: getProjectColor('Sprint Planning'),
    status: 'in progress',
    costCentres: getMockProjectCostCentres('pd8'),
    companyId: 'company-4',
  },
  {
    id: 'pd9',
    name: 'Data Analytics Dashboard',
    clientId: 'c3',
    clientName: 'HealthTech Solutions',
    billingCode: 'HTS-2024-009',
    projectManager: 'James Anderson',
    color: getProjectColor('Data Analytics Dashboard'),
    status: 'planning',
    costCentres: getMockProjectCostCentres('pd9'),
    companyId: 'company-5',
  },
  {
    id: 'pd10',
    name: 'Cloud Migration',
    clientId: 'c6',
    clientName: 'GreenEnergy Co',
    billingCode: 'GEC-2024-005',
    projectManager: 'Robert Taylor',
    color: getProjectColor('Cloud Migration'),
    status: 'on hold',
    costCentres: getMockProjectCostCentres('pd10'),
    companyId: 'company-5',
  },
  {
    id: 'pd11',
    name: 'Security Audit',
    clientId: 'c2',
    clientName: 'Global Finance Ltd',
    billingCode: 'GFL-2024-017',
    projectManager: 'David Kim',
    color: getProjectColor('Security Audit'),
    status: 'cancelled',
    costCentres: getMockProjectCostCentres('pd11'),
    companyId: 'company-2',
  },
  {
    id: 'pd12',
    name: 'Internal Training',
    clientId: 'c7',
    clientName: 'Internal',
    billingCode: 'INT-2024-001',
    projectManager: 'Emily Rodriguez',
    color: getProjectColor('Internal Training'),
    status: 'in progress',
    costCentres: getMockProjectCostCentres('pd12'),
    companyId: 'company-5',
  },
];

// Company mappings for demo mode — distributes mock data across mock companies (IDs 'company-1'–'company-5')
export const MOCK_RESOURCE_COMPANY_MAP: Record<string, string> = {
  'unallocated': 'company-1',
  '1': 'company-1', '2': 'company-1', '3': 'company-1', '4': 'company-1', '17': 'company-1', '18': 'company-1',
  '5': 'company-2', '6': 'company-2', '7': 'company-2',
  '8': 'company-3', '9': 'company-3', '10': 'company-3',
  '11': 'company-4', '12': 'company-4', '13': 'company-4',
  '14': 'company-5', '15': 'company-5', '16': 'company-5',
};

export const MOCK_PROJECT_COMPANY_MAP: Record<string, string> = {
  'pd1': 'company-1', 'pd2': 'company-1',
  'pd3': 'company-2', 'pd4': 'company-2', 'pd11': 'company-2',
  'pd5': 'company-3', 'pd6': 'company-3',
  'pd7': 'company-4', 'pd8': 'company-4',
  'pd9': 'company-5', 'pd10': 'company-5', 'pd12': 'company-5',
};

export const MOCK_CLIENT_COMPANY_MAP: Record<string, string> = {
  'c1': 'company-1', 'c2': 'company-2',
  'c3': 'company-3', 'c4': 'company-3',
  'c5': 'company-4', 'c6': 'company-4',
  'c7': 'company-5',
};

export const MOCK_COST_CENTRE_COMPANY_MAP: Record<string, string> = {
  'cc1': 'company-1', 'cc2': 'company-1', 'cc3': 'company-1',
  'cc4': 'company-2', 'cc5': 'company-2', 'cc6': 'company-2',
  'cc7': 'company-3', 'cc8': 'company-3', 'cc9': 'company-3',
  'cc10': 'company-4', 'cc11': 'company-4', 'cc12': 'company-4',
  'cc13': 'company-5', 'cc14': 'company-5', 'cc15': 'company-5',
};

// Mock Scheduler Groups - groups of resources for organizing the scheduler view
// Each group is scoped to a company via companyId
export const mockSchedulerGroups = [
  {
    id: 'sg-1',
    name: 'Team Alpha',
    resourceIds: ['1', '3', '4', '9', '12', '18'],
    avatarUrl: undefined,
    companyId: 'company-1',
  },
  {
    id: 'sg-2',
    name: 'Team Beta',
    resourceIds: ['2', '6', '7', '10', '14', '15'],
    avatarUrl: undefined,
    companyId: 'company-1',
  },
  {
    id: 'sg-3',
    name: 'Team Gamma',
    resourceIds: ['5', '8', '11', '13'],
    avatarUrl: undefined,
    companyId: 'company-2',
  },
  {
    id: 'sg-system',
    name: 'System',
    resourceIds: ['unallocated'],
    avatarUrl: undefined,
    companyId: 'company-1',
  },
  {
    id: 'sg-4',
    name: 'Team Delta',
    resourceIds: ['8', '9', '10'],
    avatarUrl: undefined,
    companyId: 'company-3',
  },
  {
    id: 'sg-5',
    name: 'Team Epsilon',
    resourceIds: ['11', '12', '13'],
    avatarUrl: undefined,
    companyId: 'company-4',
  },
  {
    id: 'sg-6',
    name: 'Team Zeta',
    resourceIds: ['14', '15', '16'],
    avatarUrl: undefined,
    companyId: 'company-5',
  },
];

// =============================================================================
// DEMO PLAN PREVIEW SCENARIO
// Displayed on the calendar in demo mode as the default "suggested bookings"
// overlay. Represents the AI-proposed Mobile App Redesign resourcing plan.
// =============================================================================

export const demoPlanPreviewScenario: PlanPreviewScenario = {
  requestId: 'demo-req-mobile-app',
  requestProjectName: 'Mobile App Redesign',
  requestRole: 'UI/UX Designer',
  scenario: {
    id: 'demo-scenario-1',
    strategy: 'spread-load',
    label: 'Mobile App Redesign',
    description: 'Spread the Mobile App Redesign work across the available team',
    icon: 'layout',
    totalHours: 320,
    coveragePercent: 100,
    resourceCount: 6,
    earliestStart: '2026-06-09',
    latestEnd: '2026-07-04',
    pros: ['Balanced workload', 'All team members contribute', 'No single point of failure'],
    cons: ['Requires coordination across six people'],
    slots: [
      // Alex Turner -- no conflict (2026-06-23 to 2026-06-27)
      {
        id: 'slot-mar-1',
        resourceId: '17',
        resourceName: 'Alex Turner',
        resourceRole: 'Frontend Developer',
        resourcePhotoUrl: 'https://i.pravatar.cc/150?img=68',
        softSignalsMet: 3,
        month: 'June 2026',
        monthKey: '2026-06',
        startDate: '2026-06-23',
        endDate: '2026-06-27',
        hours: 40,
        availableHours: 40,
        matchReasons: ['Available slot', 'Strong React skills', 'Frontend specialist'],
      },
      // David Kim -- no conflict (2026-06-16 to 2026-06-20)
      {
        id: 'slot-mar-2',
        resourceId: '4',
        resourceName: 'David Kim',
        resourceRole: 'Backend Developer',
        resourcePhotoUrl: 'https://i.pravatar.cc/150?img=13',
        softSignalsMet: 2,
        month: 'June 2026',
        monthKey: '2026-06',
        startDate: '2026-06-16',
        endDate: '2026-06-20',
        hours: 37.5,
        availableHours: 37.5,
        matchReasons: ['No confirmed bookings in this window', 'Backend API capability'],
      },
      // Emily Rodriguez -- no conflict (2026-06-09 to 2026-06-10)
      {
        id: 'slot-mar-3',
        resourceId: '3',
        resourceName: 'Emily Rodriguez',
        resourceRole: 'Project Manager',
        resourcePhotoUrl: 'https://i.pravatar.cc/150?img=5',
        softSignalsMet: 3,
        month: 'June 2026',
        monthKey: '2026-06',
        startDate: '2026-06-09',
        endDate: '2026-06-10',
        hours: 16,
        availableHours: 16,
        matchReasons: ['Free window before sprint review', 'Kickoff facilitation expertise'],
      },
      // Michael Chen -- no conflict (2026-06-23 to 2026-06-27)
      {
        id: 'slot-mar-4',
        resourceId: '2',
        resourceName: 'Michael Chen',
        resourceRole: 'UI/UX Designer',
        resourcePhotoUrl: 'https://i.pravatar.cc/150?img=12',
        softSignalsMet: 3,
        month: 'June 2026',
        monthKey: '2026-06',
        startDate: '2026-06-23',
        endDate: '2026-06-27',
        hours: 40,
        availableHours: 40,
        matchReasons: ['UI/UX specialist', 'Available in this window', 'Perfect skill match'],
      },
      // Sarah Johnson -- no conflict (2026-06-30 to 2026-07-04)
      {
        id: 'slot-mar-5',
        resourceId: '1',
        resourceName: 'Sarah Johnson',
        resourceRole: 'Senior Developer',
        resourcePhotoUrl: 'https://i.pravatar.cc/150?img=1',
        softSignalsMet: 3,
        month: 'July 2026',
        monthKey: '2026-07',
        startDate: '2026-06-30',
        endDate: '2026-07-04',
        hours: 40,
        availableHours: 40,
        matchReasons: ['Free after API Integration ends', 'Senior technical lead'],
      },
      // John Doe -- no conflict (2026-06-09 to 2026-06-20)
      {
        id: 'slot-mar-6',
        resourceId: '18',
        resourceName: 'John Doe',
        resourceRole: 'Team Member',
        resourcePhotoUrl: 'https://i.pravatar.cc/150?img=70',
        softSignalsMet: 2,
        month: 'June 2026',
        monthKey: '2026-06',
        startDate: '2026-06-09',
        endDate: '2026-06-20',
        hours: 80,
        availableHours: 80,
        matchReasons: ['Fully available in June', 'No existing bookings'],
      },
      // DELIBERATE CONFLICT: David Kim -- overlaps Infrastructure Setup (2026-06-09 to 2026-06-13)
      {
        id: 'slot-mar-7',
        resourceId: '4',
        resourceName: 'David Kim',
        resourceRole: 'Backend Developer',
        resourcePhotoUrl: 'https://i.pravatar.cc/150?img=13',
        softSignalsMet: 2,
        month: 'June 2026',
        monthKey: '2026-06',
        startDate: '2026-06-09',
        endDate: '2026-06-13',
        hours: 37.5,
        availableHours: 0,
        matchReasons: ['Overlap warning -- conflicts with Infrastructure Setup'],
      },
      // DELIBERATE CONFLICT: Sarah Johnson -- overlaps API Integration (2026-06-23 to 2026-06-27)
      {
        id: 'slot-mar-8',
        resourceId: '1',
        resourceName: 'Sarah Johnson',
        resourceRole: 'Senior Developer',
        resourcePhotoUrl: 'https://i.pravatar.cc/150?img=1',
        softSignalsMet: 1,
        month: 'June 2026',
        monthKey: '2026-06',
        startDate: '2026-06-23',
        endDate: '2026-06-27',
        hours: 40,
        availableHours: 0,
        matchReasons: ['Overlap warning -- conflicts with API Integration booking'],
      },
    ],
  },
};
